import '@livekit/components-styles'
import { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  GridLayout,
  ParticipantTile,
  FocusLayoutContainer,
  FocusLayout,
  CarouselLayout,
  useMaybeTrackRefContext,
} from '@livekit/components-react'
import { Track, AudioPresets, ScreenSharePresets, VideoPresets, VideoQuality, LocalAudioTrack, RoomEvent, ParticipantEvent } from 'livekit-client'
import type { AudioCaptureOptions, ScreenShareCaptureOptions, TrackPublishOptions, RemoteTrackPublication, LocalTrackPublication } from 'livekit-client'
import { useAntiLarsen } from './useAntiLarsen'
import { buildPipelineConfig } from './useAudioControl'
import { BANDS, readBandsFromAnalyser } from './antiLarsenProcessor'
import { useQos } from './useQos'
import { apiFetch } from '../lib/apiFetch'
import { useSubtitles } from './useSubtitles'
import SubtitleBar from './SubtitleBar'
import { ParticipantList } from './ParticipantList'
import { useReactions, ReactionPicker, ReactionBubble } from './ReactionOverlay'
import { StatsPanel } from './StatsPanel'
import { loadBenchProfile } from './bench/AVBench'
import { VisioChatPanel } from './VisioChatPanel'
import { VisioDataChat } from './VisioDataChat'
import { Whiteboard } from './Whiteboard'
import { createWhiteboardProvider } from './whiteboard-provider'
import { BreakoutSetup } from './BreakoutSetup'
import { useBreakout } from './useBreakout'
import { parseDataMessage } from './visioDataTypes'
import { logVisioEvent } from './logVisioEvent'
import { useVirtualBackground } from './useVirtualBackground'
import { VirtualBgMenu } from './VirtualBgMenu'
import { useAnnotation } from './useAnnotation'
import { AnnotationOverlay } from './AnnotationOverlay'
import { useKeyboardShortcuts } from './useKeyboardShortcuts'
import { ShortcutsHelp } from './ShortcutsHelp'
import { useDenoiser } from '../audio/useDenoiser'
import { denoiseEngine } from '../audio/DenoiseEngine'
import type { DenoisePreset } from '../audio/DenoiseEngine'
import { denoiseLog } from '../audio/denoiseLog'
import { CaptionOverlay } from './CaptionOverlay'
import type { CaptionEntry, CaptionSize } from './CaptionOverlay'
import type { CaptionMessage } from './visioDataTypes'
import { MeetingNotes } from './MeetingNotes'

// ── Hand raised overlay on video tiles ─────────────────
// Uses createPortal to inject overlay into ParticipantTile's DOM
// so ParticipantTile remains a direct grid child (no wrapper div).
function HandRaisedTile() {
  const trackRef = useMaybeTrackRefContext()
  const tileRef = useRef<HTMLDivElement>(null)
  const [handRaised, setHandRaised] = useState(false)
  const [layerInfo, setLayerInfo] = useState('')
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0)

  // Force re-render after mount so tileRef.current is available for portal
  useEffect(() => { forceUpdate() }, [])

  useEffect(() => {
    const participant = trackRef?.participant
    if (!participant) return
    const checkMeta = () => {
      try {
        const meta = JSON.parse(participant.metadata || '{}')
        setHandRaised(!!meta.handRaised)
      } catch { setHandRaised(false) }
    }
    checkMeta()
    participant.on(ParticipantEvent.ParticipantMetadataChanged, checkMeta)
    return () => { participant.off(ParticipantEvent.ParticipantMetadataChanged, checkMeta) }
  }, [trackRef?.participant])

  // Simulcast layer indicator — poll every 2s
  useEffect(() => {
    const participant = trackRef?.participant
    if (!participant) return

    const isLocal = participant.isLocal
    const poll = async () => {
      try {
        if (isLocal) {
          // LOCAL: check RTCRtpSender encodings for active simulcast layers
          const pub = participant.getTrackPublication(Track.Source.Camera)
          const sender = (pub?.track as any)?.sender as RTCRtpSender | undefined
          if (!sender) { setLayerInfo(''); return }
          const params = sender.getParameters()
          const encs = params.encodings || []
          if (encs.length === 0) { setLayerInfo(''); return }
          // Build layer string: main track resolution from getSettings()
          const settings = (pub?.track as any)?.mediaStreamTrack?.getSettings?.()
          const mainH = settings?.height || 0
          const fps = settings?.frameRate ? Math.round(settings.frameRate) : 0
          // Map rid → nominal height; LiveKit uses rid: q (low), h (medium), f (full)
          const ridToH: Record<string, number> = { q: 180, h: 360, f: 720 }
          // If only 1 encoding, show just the main resolution
          if (encs.length <= 1) {
            const h = mainH || (encs[0] as any)?.height || 0
            setLayerInfo(`${h || '?'}@${fps}`)
            return
          }
          // Multiple encodings — show active layers or --- for inactive
          const parts: string[] = []
          for (const enc of encs) {
            const rid = (enc as any).rid || ''
            const h = ridToH[rid] || ((enc as any).scaleResolutionDownBy ? Math.round(mainH / ((enc as any).scaleResolutionDownBy || 1)) : 0)
            if (enc.active === false) {
              parts.push('---')
            } else {
              parts.push(String(h || '?'))
            }
          }
          setLayerInfo(`${parts.join(' ')} @${fps}`)
        } else {
          // REMOTE: check received video dimensions
          const pub = participant.getTrackPublication(Track.Source.Camera)
          if (!pub?.track) { setLayerInfo(''); return }
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined
          if (!mst) { setLayerInfo(''); return }
          const settings = mst.getSettings?.()
          const h = settings?.height || 0
          const fps = settings?.frameRate ? Math.round(settings.frameRate) : 0
          // Map height to closest named layer
          let layer = ''
          if (h <= 0) layer = '?'
          else if (h <= 200) layer = '180'
          else if (h <= 400) layer = '360'
          else if (h <= 800) layer = '720'
          else layer = String(h)
          setLayerInfo(`${layer}p @${fps}`)
        }
      } catch { setLayerInfo('') }
    }
    poll()
    const id = setInterval(poll, 2000)
    return () => clearInterval(id)
  }, [trackRef?.participant])

  return (
    <>
      <ParticipantTile ref={tileRef} />
      {handRaised && tileRef.current && createPortal(
        <div
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, background: 'rgba(234,179,8,0.8)', borderRadius: '9999px', padding: 6, boxShadow: '0 4px 6px rgba(0,0,0,0.3)', pointerEvents: 'none' }}
          title="Main levee"
        >
          <svg viewBox="0 0 24 24" fill="white" style={{ width: 20, height: 20 }}>
            <path d="M7 11.5V14a1 1 0 01-2 0V7.5a1.5 1.5 0 013 0V11h.5a1 1 0 011 1v1.5a1 1 0 01-2 0V12h-.5zM11 11V5.5a1.5 1.5 0 013 0V11h1V4.5a1.5 1.5 0 013 0V11h1V6.5a1.5 1.5 0 013 0V15a7 7 0 01-14 0v-4z"/>
          </svg>
        </div>,
        tileRef.current
      )}
      {layerInfo && tileRef.current && createPortal(
        <div
          style={{
            position: 'absolute', bottom: 4, right: 4, zIndex: 10,
            background: 'rgba(0,0,0,0.7)', borderRadius: 4,
            padding: '1px 5px', pointerEvents: 'none',
            fontFamily: 'monospace', fontSize: 10, lineHeight: '14px',
            color: '#a1a1aa', letterSpacing: '0.5px',
          }}
        >
          {layerInfo}
        </div>,
        tileRef.current
      )}
    </>
  )
}

// ── Types ──────────────────────────────────────────────

// Audio simulcast tiers (like video 180/360/720)
type AudioTier = '12k' | '24k' | '48k'
const AUDIO_TIERS: { key: AudioTier; label: string; bitrate: number }[] = [
  { key: '12k', label: '12',  bitrate: 12_000 },
  { key: '24k', label: '24',  bitrate: 24_000 },
  { key: '48k', label: '48',  bitrate: 48_000 },
]
// Track name prefix for additional audio simulcast tracks
const AQ_PREFIX = 'aq_'

// Video simulcast tiers — selecting a tier determines which layers are sent:
// 180p = only 180p (1 layer), 360p = 180+360 (2 layers), 720p = 180+360+720 (3 layers),
// auto = 720p capture + bandwidth-adaptive layer control
type VideoTier = '180p' | '360p' | '720p' | 'auto'
const VIDEO_TIERS: { key: VideoTier; label: string }[] = [
  { key: '180p', label: '180p' },
  { key: '360p', label: '360p' },
  { key: '720p', label: '720p' },
  { key: 'auto', label: 'Auto' },
]

// Legacy export kept for compatibility
export type AudioQualityKey = '12k' | '24k' | 'auto'

// Video recv quality modes
type VideoRecvMode = 'auto' | 'low' | 'mid' | 'high'
const VIDEO_RECV_OPTIONS: { key: VideoRecvMode; label: string }[] = [
  { key: 'auto', label: 'Auto' },
  { key: 'low', label: '180' },
  { key: 'mid', label: '360' },
  { key: 'high', label: '720' },
]

// Video send: capture resolution for each tier
const VIDEO_SEND_DIMS: Record<VideoTier, { width: number; height: number }> = {
  '180p': { width: 320, height: 180 },
  '360p': { width: 640, height: 360 },
  '720p': { width: 1280, height: 720 },
  'auto': { width: 1280, height: 720 },
}

const FPS_OPTIONS = [1, 10, 15, 22, 30] as const
type FpsVal = typeof FPS_OPTIONS[number]

// Auto-adaptation thresholds (kbps upload) — only active in 'auto' tier
// Below LOW_THRESH → 1 layer, below MED_THRESH → 2 layers, above → all layers
const BW_LOW_THRESH = 200   // kbps — below this, keep only 1 layer (180p)
const BW_MED_THRESH = 400   // kbps — below this, keep 2 layers (180+360)
// Hysteresis: upgrade requires higher BW than the downgrade threshold
// Prevents oscillation when BW is marginal (e.g. 3 layers saturates to 180, 2 layers frees to 600, repeat)
const BW_UPGRADE_THRESH = 700 // kbps — must sustain this to upgrade layers
// Debounce: consecutive polls before acting
const BW_DOWN_DEBOUNCE = 5    // 10s before downgrade
const BW_UP_DEBOUNCE = 15     // 30s before upgrade — much slower to prevent oscillation


// Layout modes
type LayoutMode = 'grid' | 'focus' | 'fullscreen'

interface VisioRoomProps {
  token: string
  serverUrl: string
  onDisconnect: () => void
  roomMode?: '1to1' | 'group'
  roomId?: string
  isGuest?: boolean
  inviteToken?: string
  role?: string  // 'admin' | 'organizer' | 'participant' | 'guest'
  permissions?: Record<string, boolean>
  channelId?: string
  onPipChange?: (active: boolean) => void
  onMinimize?: (mode: 'compact' | 'floating') => void
  onStateReport?: (state: { micOn: boolean; camOn: boolean; participants: number; speakerName: string; speakerTrack: MediaStreamTrack | null }) => void
}

// ── Main wrapper ───────────────────────────────────────

export function VisioRoom({ token, serverUrl, onDisconnect, roomMode, roomId, isGuest, inviteToken, role, permissions, channelId, onPipChange, onMinimize, onStateReport }: VisioRoomProps) {
  const [activeToken, setActiveToken] = useState(token)
  const [activeServerUrl, setActiveServerUrl] = useState(serverUrl)

  return (
    <LiveKitRoom
      key={activeToken}
      token={activeToken}
      serverUrl={activeServerUrl}
      connect={true}
      video={true}
      audio={{
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      }}
      options={{
        publishDefaults: {
          audioPreset: AudioPresets.speech,
          dtx: true,
          red: true,
          videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
          screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
          screenShareSimulcastLayers: [ScreenSharePresets.h720fps15],
        },
        dynacast: true,
      }}
      onDisconnected={onDisconnect}
      style={{ height: '100%', width: '100%', overflow: 'visible' }}
    >
      <VisioConference roomMode={roomMode} roomId={roomId} token={token} isGuest={isGuest} inviteToken={inviteToken} role={role} permissions={permissions} channelId={channelId} setActiveToken={setActiveToken} setActiveServerUrl={setActiveServerUrl} onPipChange={onPipChange} onMinimize={onMinimize} onStateReport={onStateReport} />
    </LiveKitRoom>
  )
}

// ── Conference layout + controls ───────────────────────

function VisioConference({ roomMode, roomId, token, isGuest, inviteToken, role, permissions, channelId, setActiveToken, setActiveServerUrl, onPipChange, onMinimize, onStateReport }: { roomMode?: string; roomId?: string; token?: string; isGuest?: boolean; inviteToken?: string; role?: string; permissions?: Record<string, boolean>; channelId?: string; setActiveToken: (t: string) => void; setActiveServerUrl: (u: string) => void; onPipChange?: (active: boolean) => void; onMinimize?: (mode: 'compact' | 'floating') => void; onStateReport?: (state: { micOn: boolean; camOn: boolean; participants: number; speakerName: string; speakerTrack: MediaStreamTrack | null }) => void }) {
  const room = useRoomContext()
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ])
  const { localParticipant } = useLocalParticipant()
  const participants = useParticipants()

  // Auto-rejoin after too many reconnections to avoid degraded state
  const reconnectCount = useRef(0)
  useEffect(() => {
    const onReconnecting = () => {
      reconnectCount.current++
      console.warn(`[VisioRoom] reconnecting (${reconnectCount.current})`)
      logEvt('room.reconnecting', { attempt: reconnectCount.current })
      if (reconnectCount.current >= 5) {
        reconnectCount.current = 0
        const rejoin = async () => {
          try {
            const endpoint = isGuest && inviteToken
              ? `/api/visio/guest/join/${inviteToken}`
              : `/api/visio/rooms/${roomId}/join`
            const res = await apiFetch(endpoint, { method: 'POST' })
            if (res.ok) {
              const data = await res.json()
              if (data.token) {
                console.warn('[VisioRoom] forcing clean rejoin after 5 reconnections')
                setActiveToken(data.token)
                if (data.url) setActiveServerUrl(data.url)
              }
            }
          } catch (err) {
            console.warn('[VisioRoom] rejoin failed:', err)
            logEvt('error.rejoin', { error: String(err) })
          }
        }
        rejoin()
      }
    }
    const onReconnected = () => {
      // Reset counter on successful reconnect
      reconnectCount.current = Math.max(0, reconnectCount.current - 1)
      logEvt('room.reconnected')
    }
    const onDisconnected = (reason?: unknown) => {
      logEvt('room.disconnected', { reason: String(reason ?? 'unknown').slice(0, 256) })
    }
    const onMediaDevicesError = (error: Error, kind?: MediaDeviceKind) => {
      logEvt('device.error', { kind: kind ?? 'unknown', error: error.message.slice(0, 512) })
    }
    const onConnectionQualityChanged = (quality: string, participant: { identity?: string }) => {
      if (participant?.identity !== localParticipant.identity) return
      logEvt('connection_quality_changed', { quality })
      if (quality === 'poor' || quality === 'lost') {
        logEvt('qos_degraded', { reason: 'connection_quality', quality })
      }
    }
    room.on(RoomEvent.Reconnecting, onReconnecting)
    room.on(RoomEvent.Reconnected, onReconnected)
    room.on(RoomEvent.Disconnected, onDisconnected)
    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError)
    room.on(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged)
    return () => {
      room.off(RoomEvent.Reconnecting, onReconnecting)
      room.off(RoomEvent.Reconnected, onReconnected)
      room.off(RoomEvent.Disconnected, onDisconnected)
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError)
      room.off(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged)
    }
  }, [room, roomId, isGuest, inviteToken, localParticipant.identity, setActiveToken, setActiveServerUrl])

  // Role-based access
  const isAdmin = role === 'admin' || role === 'organizer'

  // Permissions — admins always have full access
  const perms = {
    can_share_screen: isAdmin || permissions?.can_share_screen !== false,
    can_raise_hand: isAdmin || permissions?.can_raise_hand !== false,
    can_react: isAdmin || permissions?.can_react !== false,
    can_unmute_video: isAdmin || permissions?.can_unmute_video !== false,
    can_unmute_audio: isAdmin || permissions?.can_unmute_audio !== false,
  }

  // Event logging helper
  const logEvt = useCallback((eventType: string, eventData?: Record<string, unknown>) => {
    logVisioEvent(roomId, eventType, eventData, isGuest ? { isGuest: true, guestId: localParticipant.identity } : undefined)
  }, [roomId, isGuest, localParticipant.identity])

  // Log room join on mount, room leave on unmount
  useEffect(() => {
    logEvt('room.joined', { role, roomMode, participants: participants.length })
    return () => { logEvt('room.left') }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Menu state — only one menu open at a time
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  // Panel state (side panels)
  const [showParticipants, setShowParticipants] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [showWhiteboard, setShowWhiteboard] = useState(false)
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  const [showNotes, setShowNotes] = useState(false)
  // Caption overlay state
  const [showCaptionOverlay, setShowCaptionOverlay] = useState(false)
  const [captionSize, setCaptionSize] = useState<CaptionSize>(() => {
    try { const s = localStorage.getItem('visio-caption-size'); if (s === 'small' || s === 'medium' || s === 'large') return s } catch {}
    return 'medium'
  })
  const [captionEntries, setCaptionEntries] = useState<CaptionEntry[]>([])
  const captionIdRef = useRef(0)
  // Breakout hook (M004 cycle 2 — extracted from inline logic)
  const {
    breakoutActive, breakoutRoomN, timerSec,
    showBreakoutSetup, setShowBreakoutSetup,
    startBreakout, recallBreakout,
  } = useBreakout({
    roomId, isAdmin, room, localParticipant,
    setActiveToken, setActiveServerUrl, logEvt,
  })

  // Layout mode
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid')
  const [hideNoVideo, setHideNoVideo] = useState(false)

  // Hand raised
  const [handRaised, setHandRaised] = useState(false)

  // Copy link feedback
  const [linkCopied, setLinkCopied] = useState(false)

  // Reactions
  const { activeReactions, sendReaction, REACTIONS: reactionList } = useReactions()

  // Admin mute notification
  const [muteNotification, setMuteNotification] = useState<string | null>(null)

  // Room-level permissions (admin can toggle)
  const [roomPerms, setRoomPerms] = useState<Record<string, boolean>>({
    can_share_screen: true, can_raise_hand: true, can_react: true,
    can_unmute_video: true, can_unmute_audio: true,
  })

  // Audio processing (advanced settings)
  const [noiseSuppression, setNoiseSuppression] = useState(true)
  const [echoCancellation, setEchoCancellation] = useState(true)

  // Moment DeepFilterNet3 noise suppression (ONNX via Web Worker + AudioWorklet)
  const denoiser = useDenoiser('visio')

  // A/V sync mode: both modes sync audio+video, difference is total latency
  // 'sync' = fast denoise (145ms) + 145ms video delay
  // 'async' = quality denoise (630ms) + 630ms video delay
  const [avSyncMode, setAvSyncMode] = useState<DenoisePreset>(denoiseEngine.preset)

  // Virtual background — pass logEvt so init/error/first-mask land in
  // visio_events (useful for remote diagnostics on iPad / Safari where
  // we can't attach DevTools).
  const vbg = useVirtualBackground(logEvt)
  const vbgProcessRef = useRef(vbg.processFrame)
  vbgProcessRef.current = vbg.processFrame
  const vbgReadyRef = useRef(vbg.ready)
  vbgReadyRef.current = vbg.ready
  const vbgModeRef = useRef(vbg.mode)
  vbgModeRef.current = vbg.mode

  // Use bench-measured delay if available (per (DN, VBG) cell), otherwise fall back to engine estimate.
  // Table 2D: syncTable[dn][vbg] = Δ = V - A on outgoing streams. Video delay = max(0, -Δ).
  // When denoiser is OFF, no DN latency to compensate — force delay to 0.
  const benchProfile = loadBenchProfile()
  // Bench table only has off/sync/async cells — fast1 is new, falls back to estimate.
  const rawAvSyncDelayMs = (benchProfile && avSyncMode !== 'fast1')
    ? (() => {
        const dn = avSyncMode === 'async' ? 'async' : avSyncMode === 'sync' ? 'sync' : 'off'
        // useVirtualBackground defaults to GPU inference; no CPU toggle exposed, so map any active VBG to 'gpu'.
        const vb: 'none' | 'gpu' | 'cpu' = vbg.mode === 'none' ? 'none' : 'gpu'
        const delta = benchProfile.syncTable[dn][vb] ?? 0
        return Math.max(0, -delta)
      })()
    : denoiseEngine.estimatedLatencyMs
  const avSyncDelayMs = denoiser.mode === 'off' ? 0 : rawAvSyncDelayMs

  // Screenshare annotations
  const annot = useAnnotation()

  // ── Audio controls (ETAPE 11) ──
  const [micMode, setMicMode] = useState<'auto' | 'manual'>('auto')
  const [micLevel, setMicLevel] = useState(5)           // 0-10
  const [remoteMode, setRemoteMode] = useState<'auto' | 'manual'>('auto')
  const [remoteLevel, setRemoteLevel] = useState(5)     // 0-10
  const [speakerMode, setSpeakerMode] = useState<'antilarsen' | 'manual'>('manual')
  const [speakerLevel, setSpeakerLevel] = useState(5)   // 0-10
  const [speakerMuted, setSpeakerMuted] = useState(false)

  // ── Simulcast controls ──
  // Send: video resolution (capture + simulcast combined), audio max tier
  const [videoSendRes, setVideoSendRes] = useState<VideoTier>('auto')
  const [videoSendFps, setVideoSendFps] = useState<FpsVal>(30)
  const [audioSendMax, setAudioSendMax] = useState<AudioTier>('48k')
  // Receive: 'auto' = SFU decides, or force specific quality
  const [audioRecvForce, setAudioRecvForce] = useState<AudioTier>('48k')
  const [videoRecvForce, setVideoRecvForce] = useState<VideoRecvMode>('auto')
  // Effective layer count: 180p→1, 360p→2, 720p→3, auto→bandwidth-adaptive
  const [effectiveLayers, setEffectiveLayers] = useState(3)
  const effectiveLayersRef = useRef(3)
  const bwDebounceRef = useRef({ lowCount: 0, medCount: 0, okCount: 0 })
  // Send/recv bitrate indicators (kbps)
  const [sendBitrateKbps, setSendBitrateKbps] = useState(0)
  const [recvBitrateKbps, setRecvBitrateKbps] = useState(0)
  // Refs for additional audio tracks published (simulcast)
  const audioSimTrackRefs = useRef<{ tier: AudioTier; pub: LocalTrackPublication }[]>([])

  // ── Video controls ──
  const [camDeviceVersion, setCamDeviceVersion] = useState(0)

  // Audio pipeline config (single source of truth: useAudioControl)
  const pipelineConfig = buildPipelineConfig({ micMode, micLevel, remoteMode, remoteLevel, speakerMode, speakerLevel, speakerMuted })
  const larsenStatus = useAntiLarsen(pipelineConfig)

  // QoS: audio priority, video degradation, buffer resilience
  const qosStatus = useQos(true, logEvt)

  // Initialize room permissions from prop
  useEffect(() => {
    if (permissions && Object.keys(permissions).length > 0) {
      setRoomPerms(prev => ({ ...prev, ...permissions }))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup all media on unmount (camera, mic, simulcast tracks)
  useEffect(() => {
    return () => {
      // Stop simulcast audio tracks
      audioSimTrackRefs.current.forEach(({ pub }) => {
        try {
          if (pub.track?.mediaStreamTrack) pub.track.mediaStreamTrack.stop()
          localParticipant.unpublishTrack(pub.track!)
        } catch {}
      })
      audioSimTrackRefs.current = []
      // Stop mic pipeline AudioContext
      if (micPipelineRef.current) {
        try { micPipelineRef.current.ctx.close() } catch {}
        micPipelineRef.current = null
      }
      // Explicitly disable mic and camera to release hardware
      try { localParticipant.setCameraEnabled(false) } catch {}
      try { localParticipant.setMicrophoneEnabled(false) } catch {}
      try { localParticipant.setScreenShareEnabled(false) } catch {}
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Publish role in participant metadata (so other clients see it).
  // Must wait for the LiveKit signal connection to be established — calling
  // setMetadata before room.state === 'connected' produces "cannot send
  // signal request before connected" and times out 5 s later. Also republish
  // on reconnect, because the server treats reconnects as a fresh session
  // for metadata in some cases.
  useEffect(() => {
    if (!role) return
    let cancelled = false

    const publish = async () => {
      if (cancelled) return
      try {
        const meta = JSON.parse(localParticipant.metadata || '{}')
        if (meta.role === role) return // already up to date
        await localParticipant.setMetadata(JSON.stringify({ ...meta, role }))
      } catch (err) {
        console.warn('[VisioRoom] setMetadata (role) failed:', err)
      }
    }

    if (room.state === 'connected') {
      publish()
    } else {
      // Gate on the Connected event instead of a fixed 1s setTimeout.
      const onConnected = () => { publish() }
      room.once(RoomEvent.Connected, onConnected)
      // Also republish on Reconnected (server may drop metadata on reconnect).
      room.on(RoomEvent.Reconnected, publish)
      return () => {
        cancelled = true
        room.off(RoomEvent.Connected, onConnected)
        room.off(RoomEvent.Reconnected, publish)
      }
    }
    // Already connected path — still arm Reconnected handler for future drops.
    room.on(RoomEvent.Reconnected, publish)
    return () => {
      cancelled = true
      room.off(RoomEvent.Reconnected, publish)
    }
  }, [role, room]) // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle a room-level permission and persist via API
  const togglePermission = useCallback(async (key: string) => {
    if (!roomId) return
    const newPerms = { ...roomPerms, [key]: !roomPerms[key] }
    setRoomPerms(newPerms)
    logEvt('permission.toggle', { key, value: newPerms[key] })
    try {
      await apiFetch(`/api/visio/rooms/${roomId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: newPerms }),
      })
    } catch (e) {
      console.warn('[VisioRoom] togglePermission', e)
      logEvt('error.permission', { key, error: String(e) })
    }
  }, [roomId, roomPerms])

  // Hand raise toggle — publish via metadata
  const toggleHand = useCallback(() => {
    const newVal = !handRaised
    setHandRaised(newVal)
    logEvt(newVal ? 'hand.raised' : 'hand.lowered')
    try {
      const meta = JSON.parse(localParticipant.metadata || '{}')
      localParticipant.setMetadata(JSON.stringify({
        ...meta,
        handRaised: newVal,
        handRaisedAt: newVal ? Date.now() : undefined,
      }))
    } catch {
      localParticipant.setMetadata(JSON.stringify({ handRaised: newVal, handRaisedAt: newVal ? Date.now() : undefined }))
    }
  }, [handRaised, localParticipant, logEvt])

  // Listen for admin mute + whiteboard via DataChannel (breakout handled by useBreakout)
  useEffect(() => {
    if (!room) return
    const handler = (payload: Uint8Array) => {
      const data = parseDataMessage(payload)
      if (!data) return
      if (data.type === 'admin_mute' && data.track === 'audio') {
        localParticipant.setMicrophoneEnabled(false)
        setMuteNotification("L'organisateur a coupe votre micro")
        setTimeout(() => setMuteNotification(null), 4000)
        logEvt('admin.muted', { track: 'audio' })
      }
      if (data.type === 'whiteboard_on') {
        setShowWhiteboard(true)
        setLayoutMode('focus')
        setShowParticipants(false)
        setShowStats(false)
        setShowChat(false)
      }
      if (data.type === 'whiteboard_off') {
        setShowWhiteboard(false)
      }
      // Caption overlay: receive captions from other participants
      if (data.type === 'caption' && showCaptionOverlay) {
        const cap = data as CaptionMessage
        if (cap.from === localParticipant.identity) return // skip own
        setCaptionEntries(prev => {
          if (cap.final) {
            captionIdRef.current++
            const without = prev.filter(c => c.id !== `remote-${cap.from}-partial`)
            return [...without, { id: `remote-${captionIdRef.current}`, from: cap.from, text: cap.text, final: true, ts: Date.now() }].slice(-10)
          } else {
            const without = prev.filter(c => c.id !== `remote-${cap.from}-partial`)
            return [...without, { id: `remote-${cap.from}-partial`, from: cap.from, text: cap.text, final: false, ts: Date.now() }].slice(-10)
          }
        })
      }
    }
    room.on(RoomEvent.DataReceived, handler)
    return () => { room.off(RoomEvent.DataReceived, handler) }
  }, [room, localParticipant, showCaptionOverlay])

  // Copy invite link
  const copyInviteLink = useCallback(() => {
    if (!inviteToken) return
    const link = `${window.location.origin}/?visio_invite=${inviteToken}`
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    })
  }, [inviteToken])

  // Whiteboard provider (persists across open/close to keep drawings — H8)
  const whiteboardProvider = useMemo(() => {
    if (!roomId) return null
    return createWhiteboardProvider(roomId)
  }, [roomId])
  useEffect(() => {
    return () => { whiteboardProvider?.destroy() }
  }, [whiteboardProvider])

  // Layer control for bandwidth adaptation
  const videoAreaRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [layoutKey, setLayoutKey] = useState(0)

  const screenShareTracks = tracks.filter(t => t.source === Track.Source.ScreenShare)
  const cameraTracks = tracks.filter(t => t.source === Track.Source.Camera)

  const isMicOn = localParticipant.isMicrophoneEnabled
  const isCamOn = localParticipant.isCameraEnabled
  const isScreenSharing = localParticipant.isScreenShareEnabled && screenShareTracks.some(t => t.participant?.identity === localParticipant.identity)

  // Screen share options
  const screenShareOpts: ScreenShareCaptureOptions = {
    contentHint: 'detail',
    resolution: ScreenSharePresets.h1080fps15.resolution,
  }
  const screenSharePublishOpts: TrackPublishOptions = {
    screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
    screenShareSimulcastLayers: [ScreenSharePresets.h720fps15],
  }

  // Auto-stop own screen share when someone else starts sharing (last share wins)
  const remoteScreenShares = screenShareTracks.filter(
    t => t.participant && t.participant.identity !== localParticipant.identity
  )
  useEffect(() => {
    if (isScreenSharing && remoteScreenShares.length > 0) {
      localParticipant.setScreenShareEnabled(false)
      logEvt('screenshare.off', { reason: 'remote_share_started' })
    }
  }, [remoteScreenShares.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch to focus mode when screen share starts, back to grid when it stops
  const screenShareFocusRef = useRef(false)
  useEffect(() => {
    if (screenShareTracks.length > 0) {
      setLayoutMode('focus')
      screenShareFocusRef.current = true
    } else if (screenShareFocusRef.current) {
      setLayoutMode('grid')
      screenShareFocusRef.current = false
      // Force video pipeline rebuild to restore camera after screenshare (H3)
      setLayoutKey(k => k + 1)
      setCamDeviceVersion(v => v + 1)
      // Clear annotations when screenshare stops
      annot.clearAll()
    }
  }, [screenShareTracks.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Observe container resize
  useEffect(() => {
    const el = videoAreaRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0
      setContainerWidth(prev => {
        if (Math.abs(prev - width) > 100) setLayoutKey(k => k + 1)
        return width
      })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // ── Video receive quality (SFU layer selection) ──
  useEffect(() => {
    if (videoRecvForce === 'auto') return  // auto: let SFU adapt based on bandwidth
    const QUALITY_MAP: Record<string, VideoQuality> = {
      low: VideoQuality.LOW,
      mid: VideoQuality.MEDIUM,
      high: VideoQuality.HIGH,
    }
    const vq = QUALITY_MAP[videoRecvForce] ?? VideoQuality.HIGH
    room?.remoteParticipants?.forEach(rp => {
      for (const [, pub] of rp.trackPublications) {
        if (pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera) {
          (pub as RemoteTrackPublication).setVideoQuality(vq)
        }
      }
    })
  }, [videoRecvForce, cameraTracks.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Audio receive quality (subscribe to correct simulcast tier) ──
  useEffect(() => {
    if (!room?.remoteParticipants) return
    room.remoteParticipants.forEach(rp => {
      for (const [, pub] of rp.trackPublications) {
        if (pub.kind !== Track.Kind.Audio) continue
        const rPub = pub as RemoteTrackPublication
        const name = pub.trackName || ''
        const isSim12 = name === `${AQ_PREFIX}12k`
        const isSim24 = name === `${AQ_PREFIX}24k`
        const isMainMic = pub.source === Track.Source.Microphone
        if (audioRecvForce === '48k') {
          if (isMainMic) rPub.setSubscribed(true)
          if (isSim12 || isSim24) rPub.setSubscribed(false)
        } else if (audioRecvForce === '24k') {
          if (isSim24) rPub.setSubscribed(true)
          if (isMainMic || isSim12) rPub.setSubscribed(false)
        } else if (audioRecvForce === '12k') {
          if (isSim12) rPub.setSubscribed(true)
          if (isMainMic || isSim24) rPub.setSubscribed(false)
        }
      }
    })
  }, [audioRecvForce, cameraTracks.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-manage subscription for newly arriving remote audio tracks
  useEffect(() => {
    if (!room) return
    const handler = (_track: any, publication: any, _participant: any) => {
      if (publication.kind !== Track.Kind.Audio) return
      const name = publication.trackName || ''
      const isSim12 = name === `${AQ_PREFIX}12k`
      const isSim24 = name === `${AQ_PREFIX}24k`
      const isMainMic = publication.source === Track.Source.Microphone
      if (audioRecvForce === '48k') {
        if (isSim12 || isSim24) (publication as RemoteTrackPublication).setSubscribed(false)
      } else if (audioRecvForce === '24k') {
        if (isMainMic || isSim12) (publication as RemoteTrackPublication).setSubscribed(false)
      } else if (audioRecvForce === '12k') {
        if (isMainMic || isSim24) (publication as RemoteTrackPublication).setSubscribed(false)
      }
    }
    room.on(RoomEvent.TrackSubscribed, handler)
    return () => { room.off(RoomEvent.TrackSubscribed, handler) }
  }, [room, audioRecvForce]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Publish main mic track (48k — always published when mic on) ──
  useEffect(() => {
    if (!isMicOn) return
    const opts: AudioCaptureOptions = {
      echoCancellation,
      noiseSuppression,
      autoGainControl: micMode === 'auto',
      channelCount: 1,
      sampleRate: 48000,
    }
    localParticipant.setMicrophoneEnabled(true, opts, {
      audioPreset: AudioPresets.music,
      dtx: true,
      red: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noiseSuppression, echoCancellation, micMode])

  // ── Audio simulcast: DISABLED ──
  // Publishing extra audio tracks (12k/24k clones) causes NegotiationError timeouts
  // that disconnect the signal and force slow reconnections. LiveKit Opus codec
  // already handles audio bitrate adaptation natively.
  useEffect(() => {
    // Cleanup any previously published simulcast tracks
    if (audioSimTrackRefs.current.length > 0) {
      audioSimTrackRefs.current.forEach(({ pub }) => {
        try { localParticipant.unpublishTrack(pub.track!) } catch {}
      })
      audioSimTrackRefs.current = []
    }
    return
  }, [isMicOn, audioSendMax]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Video send: resolution + simulcast layers ──
  // Re-publish only when resolution/fps/cam changes; NOT when effectiveLayers changes
  // Tier determines capture resolution AND number of simulcast layers:
  //   180p → 320x180, no simulcast (1 layer)
  //   360p → 640x360 + 180p simulcast (2 layers)
  //   720p → 1280x720 + 180p+360p simulcast (3 layers)
  //   auto → 1280x720 + 180p+360p simulcast (3 layers, bandwidth-adaptive)
  const prevSimulcastRef = useRef<{ res: string; fps: number }>({ res: '', fps: 0 })
  useEffect(() => {
    if (!isCamOn) return
    const dim = VIDEO_SEND_DIMS[videoSendRes]
    let allLayers: typeof VideoPresets.h180[]
    let layerCount: number
    if (videoSendRes === '180p') {
      allLayers = []
      layerCount = 1
    } else if (videoSendRes === '360p') {
      allLayers = [VideoPresets.h180]
      layerCount = 2
    } else {
      // 720p and auto both publish 3 layers
      allLayers = [VideoPresets.h180, VideoPresets.h360]
      layerCount = 3
    }
    localParticipant.setCameraEnabled(true, {
      resolution: { width: dim.width, height: dim.height, frameRate: videoSendFps },
    }, {
      videoSimulcastLayers: allLayers,
    })
    // For non-auto tiers, lock effectiveLayers to the tier's layer count
    if (videoSendRes !== 'auto') {
      effectiveLayersRef.current = layerCount
      setEffectiveLayers(layerCount)
      bwDebounceRef.current = { lowCount: 0, medCount: 0, okCount: 0 }
    }
    prevSimulcastRef.current = { res: videoSendRes, fps: videoSendFps }
    console.log(`[simulcast] ${videoSendRes} @${videoSendFps} → ${allLayers.length} simulcast + main = ${layerCount} layers`)
    logEvt('video.simulcast', { res: videoSendRes, fps: videoSendFps, simulcastLayers: allLayers.length, effectiveLayers: layerCount })
  }, [videoSendRes, videoSendFps, isCamOn]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bandwidth adaptation: toggle simulcast encodings active/inactive ──
  // Uses RTCRtpSender.setParameters() to avoid re-publishing the track
  useEffect(() => {
    if (!isCamOn) return
    const pub = localParticipant.getTrackPublication(Track.Source.Camera)
    const sender = (pub?.track as any)?.sender as RTCRtpSender | undefined
    if (!sender) return
    try {
      const params = sender.getParameters()
      if (!params.encodings || params.encodings.length <= 1) return
      // LiveKit rid order: q (low/180), h (mid/360), f (full/720)
      // effectiveLayers=3 → all active, 2 → cut 720 (heaviest), 1 → only 180
      const encs = params.encodings
      for (let i = 0; i < encs.length; i++) {
        // Keep the first N encodings active (lowest res first)
        // effectiveLayers=3: all true, =2: 180+360 only, =1: 180 only
        encs[i].active = i < effectiveLayers
      }
      sender.setParameters(params)
      const activeCount = encs.filter(e => e.active).length
      console.log(`[bw-adapt] effectiveLayers=${effectiveLayers} → ${activeCount}/${encs.length} encodings active`)
      logEvt('video.bw.layers', { effectiveLayers, activeEncodings: activeCount, totalEncodings: encs.length })
    } catch (e) {
      console.warn('[bw-adapt] setParameters failed:', e)
    }
  }, [effectiveLayers, isCamOn]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mic gain pipeline for manual volume
  const micPipelineRef = useRef<{
    ctx: AudioContext
    gain: GainNode
    analyser: AnalyserNode
    dest: MediaStreamAudioDestinationNode
    originalTrack: MediaStreamTrack
  } | null>(null)
  const [micVu, setMicVu] = useState(0) // 0-100 vu-meter
  const [micBands, setMicBands] = useState<{ label: string; energy: number }[]>([])

  // Setup/teardown mic gain pipeline (+ optional noise suppression)
  useEffect(() => {
    if (micMode === 'auto' || !isMicOn) {
      // Teardown
      const pipe = micPipelineRef.current
      if (pipe) {
        denoiseLog({ step: 'pipeline_teardown', context: 'visio', pipeline: 'manual' })
        const pub = localParticipant.getTrackPublication(Track.Source.Microphone)
        if (pub?.track && 'replaceTrack' in pub.track) {
          (pub.track as any).replaceTrack(pipe.originalTrack).catch((e: any) => logEvt('error.replaceTrack', { pipeline: 'manual', error: String(e) }))
        }
        denoiser.destroyNode()
        pipe.ctx.close().catch((e: any) => logEvt('error.ctx.close', { pipeline: 'manual', error: String(e) }))
        micPipelineRef.current = null
      }
      return
    }

    // Manual mode: create gain pipeline
    let cancelled = false // stale-closure guard for async buildChain
    const pub = localParticipant.getTrackPublication(Track.Source.Microphone)
    if (!pub?.track) return

    const originalTrack = pub.track.mediaStreamTrack
    const ctx = new AudioContext({ sampleRate: 48000 })
    ctx.resume()
    const source = ctx.createMediaStreamSource(new MediaStream([originalTrack]))
    const gain = ctx.createGain()
    gain.gain.value = micLevel / 5
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    analyser.smoothingTimeConstant = 0.5
    const dest = ctx.createMediaStreamDestination()

    source.connect(analyser) // tap for vu-meter (raw level)
    source.connect(gain)

    // Always insert NS node — worklet does passthrough when mode='off'
    const buildChain = async () => {
      const dnNode = await denoiser.createNode(ctx)
      if (cancelled) return // effect was cleaned up while we awaited
      if (dnNode) {
        gain.connect(dnNode)
        dnNode.connect(dest)
        denoiseLog({ step: 'pipeline_ns_insert', context: 'visio', pipeline: 'manual' })
      } else {
        gain.connect(dest) // fallback: no NS
        denoiseLog({ step: 'pipeline_ns_fallback', context: 'visio', pipeline: 'manual' })
      }

      micPipelineRef.current = { ctx, gain, analyser, dest, originalTrack }

      const processedTrack = dest.stream.getAudioTracks()[0]
      if (pub.track && 'replaceTrack' in pub.track) {
        (pub.track as any).replaceTrack(processedTrack).catch((e: any) => {
          console.warn('[mic-pipeline] replaceTrack failed:', e)
        })
      }
    }
    buildChain()

    return () => {
      cancelled = true
    }
  // denoiser.mode removed — mode toggle handled by worklet passthrough, no pipeline rebuild
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micMode, isMicOn])

  // Update mic gain value
  useEffect(() => {
    if (micPipelineRef.current && micMode === 'manual') {
      micPipelineRef.current.gain.gain.value = micLevel / 5
    }
  }, [micLevel, micMode])

  // Auto-mode mic: analyser for vu-meter + optional noise suppression
  const autoAnalyserRef = useRef<{ ctx: AudioContext; analyser: AnalyserNode; originalTrack?: MediaStreamTrack } | null>(null)

  useEffect(() => {
    if (micMode !== 'auto' || !isMicOn) {
      // Cleanup auto analyser + NS
      if (autoAnalyserRef.current) {
        denoiseLog({ step: 'pipeline_teardown', context: 'visio', pipeline: 'auto' })
        if (autoAnalyserRef.current.originalTrack) {
          const pub = localParticipant.getTrackPublication(Track.Source.Microphone)
          if (pub?.track && 'replaceTrack' in pub.track) {
            (pub.track as any).replaceTrack(autoAnalyserRef.current.originalTrack).catch((e: any) => logEvt('error.replaceTrack', { pipeline: 'auto-teardown', error: String(e) }))
          }
        }
        denoiser.destroyNode()
        autoAnalyserRef.current.ctx.close().catch((e: any) => logEvt('error.ctx.close', { pipeline: 'auto-teardown', error: String(e) }))
        autoAnalyserRef.current = null
      }
      return
    }
    let cancelled = false // stale-closure guard for async buildAutoNS
    const pub = localParticipant.getTrackPublication(Track.Source.Microphone)
    const mst = pub?.track?.mediaStreamTrack
    if (!mst) return
    const ctx = new AudioContext({ sampleRate: 48000 })
    ctx.resume()
    const source = ctx.createMediaStreamSource(new MediaStream([mst]))
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    analyser.smoothingTimeConstant = 0.5
    source.connect(analyser)

    // Always insert NS node — worklet does passthrough when mode='off'
    const buildAutoNS = async () => {
      const dnNode = await denoiser.createNode(ctx)
      if (cancelled) return // effect was cleaned up while we awaited
      if (dnNode) {
        const dest = ctx.createMediaStreamDestination()
        source.connect(dnNode)
        dnNode.connect(dest)
        const processedTrack = dest.stream.getAudioTracks()[0]
        if (pub.track && 'replaceTrack' in pub.track) {
          (pub.track as any).replaceTrack(processedTrack).catch((e: any) => logEvt('error.replaceTrack', { pipeline: 'auto-ns', error: String(e) }))
        }
        autoAnalyserRef.current = { ctx, analyser, originalTrack: mst }
        denoiseLog({ step: 'pipeline_ns_insert', context: 'visio', pipeline: 'auto' })
      } else {
        autoAnalyserRef.current = { ctx, analyser }
        denoiseLog({ step: 'pipeline_ns_fallback', context: 'visio', pipeline: 'auto' })
      }
    }
    buildAutoNS()

    return () => {
      cancelled = true // prevent stale buildAutoNS from calling replaceTrack
      if (autoAnalyserRef.current?.originalTrack) {
        const p = localParticipant.getTrackPublication(Track.Source.Microphone)
        if (p?.track && 'replaceTrack' in p.track) {
          (p.track as any).replaceTrack(autoAnalyserRef.current.originalTrack).catch((e: any) => logEvt('error.replaceTrack', { pipeline: 'auto-cleanup', error: String(e) }))
        }
      }
      denoiser.destroyNode()
      ctx.close().catch((e: any) => logEvt('error.ctx.close', { pipeline: 'auto-cleanup', error: String(e) }))
    }
  // denoiser.mode removed — mode toggle handled by worklet passthrough, no pipeline rebuild
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micMode, isMicOn])

  // Mic vu-meter + 7-band spectrum reader (works in both auto and manual mode)
  useEffect(() => {
    if (!isMicOn) { setMicBands([]); return }
    const id = setInterval(() => {
      let analyser: AnalyserNode | null = null
      if (micMode === 'manual' && micPipelineRef.current) {
        analyser = micPipelineRef.current.analyser
      } else if (micMode === 'auto' && autoAnalyserRef.current) {
        analyser = autoAnalyserRef.current.analyser
      }
      if (!analyser) { setMicVu(0); setMicBands([]); return }
      // Vu-meter (byte data)
      const data = new Uint8Array(analyser.frequencyBinCount)
      analyser.getByteFrequencyData(data)
      const avg = data.reduce((a, b) => a + b, 0) / data.length
      setMicVu(Math.round((avg / 255) * 100))
      // 7-band spectrum (float FFT)
      setMicBands(readBandsFromAnalyser(analyser))
    }, 100)
    return () => clearInterval(id)
  }, [micMode, isMicOn])

  // Canvas-based video resize pipeline
  // Captures raw camera → draws to canvas at target resolution → replaces what's sent + displayed
  const videoPipelineRef = useRef<{
    rawClone: MediaStreamTrack
    video: HTMLVideoElement
    canvas: HTMLCanvasElement
    previewCanvas: HTMLCanvasElement
    rafId: number
    canvasStream: MediaStream
    previewStream: MediaStream
    cleanupDelayBuf?: () => void
  } | null>(null)
  const [actualRes, setActualRes] = useState('')

  const teardownVideoPipeline = useCallback(() => {
    const pipe = videoPipelineRef.current
    if (!pipe) return
    cancelAnimationFrame(pipe.rafId)
    pipe.cleanupDelayBuf?.()
    pipe.video.pause()
    pipe.video.srcObject = null
    pipe.rawClone.stop()
    pipe.canvasStream.getTracks().forEach(t => t.stop())
    pipe.previewStream.getTracks().forEach(t => t.stop())
    videoPipelineRef.current = null
    logEvt('video.pipeline.stop')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isCamOn) {
      teardownVideoPipeline()
      setActualRes('')
      return
    }

    const timeoutId = setTimeout(async () => {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera)
      if (!pub?.track) return

      // Teardown previous pipeline before rebuilding
      teardownVideoPipeline()

      const rawMst = pub.track.mediaStreamTrack
      const dim = VIDEO_SEND_DIMS[videoSendRes]

      // Clone raw track so we can keep it running independently
      const rawClone = rawMst.clone()

      // Off-screen video playing the cloned raw camera
      const video = document.createElement('video')
      video.srcObject = new MediaStream([rawClone])
      video.muted = true
      video.playsInline = true
      try { await video.play() } catch { return }

      // Two canvases:
      //  - previewCanvas : VBG applied, NO delay    → fed to local preview (self-view)
      //  - canvas        : delayed copy of preview  → fed to outgoing WebRTC track
      //
      // VBG runs ONCE per frame on previewCanvas; the outgoing canvas just
      // drawImages a time-delayed snapshot of it. This lets the user see the
      // blur/background effect on themselves instantly while the outgoing
      // stream stays in sync with denoised audio.
      const previewCanvas = document.createElement('canvas')
      previewCanvas.width = dim.width
      previewCanvas.height = dim.height
      const previewCtx = previewCanvas.getContext('2d')!

      const canvas = document.createElement('canvas')
      canvas.width = dim.width
      canvas.height = dim.height
      const ctx = canvas.getContext('2d')!

      // Capture streams. iOS Safari is unreliable with captureStream(0) +
      // manual requestFrame() — frames stop being pushed to the track after
      // the first one. Use captureStream(fps) so the browser samples the
      // canvas on its own cadence (spec-compliant on all browsers, works on
      // iPad). Preview stream (local, no delay) and outgoing stream
      // (delayed) are kept distinct.
      const previewStream = previewCanvas.captureStream(videoSendFps)
      const previewTrack = previewStream.getVideoTracks()[0]

      const canvasStream = canvas.captureStream(videoSendFps)
      const canvasTrack = canvasStream.getVideoTracks()[0]

      // Replace at WebRTC sender level (keeps LiveKit state intact) — outgoing only
      const lkTrack = pub.track as any
      try {
        if (lkTrack.sender) {
          await lkTrack.sender.replaceTrack(canvasTrack)
        }
      } catch (err) {
        console.warn('[video-pipeline] Failed to replace sender track:', err)
      }

      // Local preview → previewCanvas (VBG visible, zero delay).
      // LiveKit re-attaches the camera track's <video> when side-effects happen
      // (screenshare publish, participant layout re-render, etc.) which resets
      // srcObject back to the raw track (no VBG). Expose a re-injector we can
      // call both at setup and every frame from the draw loop.
      const reinjectPreview = () => {
        const els = lkTrack.attachedElements as HTMLMediaElement[] | undefined
        if (!els) return
        for (const el of els) {
          const ve = el as HTMLVideoElement
          if (ve.srcObject !== previewStream) {
            ve.srcObject = previewStream
          }
        }
      }
      reinjectPreview()

      // A/V sync: delay outgoing video frames to compensate denoise audio latency.
      // Only the outgoing canvas is delayed; previewCanvas stays real-time.
      const delayFrames = avSyncDelayMs > 0 ? Math.max(1, Math.round(avSyncDelayMs / (1000 / videoSendFps))) : 0
      const delayBuf: { bmp: ImageBitmap; ts: number }[] = []

      // Draw loop using rAF with frame timing for smooth target fps
      const frameInterval = 1000 / videoSendFps
      let lastDrawTime = 0
      let rafId = 0
      const drawLoop = (now: number) => {
        rafId = requestAnimationFrame(drawLoop)
        const elapsed = now - lastDrawTime
        if (elapsed < frameInterval) return // skip — too early for next frame
        lastDrawTime = now - (elapsed % frameInterval) // drift correction
        if (video.readyState < video.HAVE_CURRENT_DATA) return

        // Re-assert local preview srcObject in case LiveKit re-attached the
        // camera element (screenshare publish, layout change). Check is a
        // cheap pointer compare; assignment only fires on actual drift.
        reinjectPreview()

        // 1) Preview: VBG or raw, drawn to previewCanvas every frame, no delay.
        if (vbgReadyRef.current && vbgModeRef.current !== 'none') {
          vbgProcessRef.current(video, previewCtx, dim.width, dim.height)
        } else {
          previewCtx.drawImage(video, 0, 0, dim.width, dim.height)
        }
        // previewTrack is driven by captureStream(fps) — no manual push needed.

        // 2) Outgoing: take a snapshot of previewCanvas (already VBG'd), push to
        //    delay buffer, then draw the oldest to the outgoing canvas.
        if (delayFrames > 0) {
          createImageBitmap(previewCanvas).then(bmp => {
            delayBuf.push({ bmp, ts: now })
            if (delayBuf.length > delayFrames) {
              const oldest = delayBuf.shift()!
              ctx.drawImage(oldest.bmp, 0, 0, dim.width, dim.height)
              oldest.bmp.close()
              // canvasTrack is driven by captureStream(fps) — no manual push needed.
            }
          }).catch(() => {})
        } else {
          // No audio delay → outgoing canvas mirrors previewCanvas in real-time.
          ctx.drawImage(previewCanvas, 0, 0, dim.width, dim.height)
          // canvasTrack is driven by captureStream(fps) — no manual push needed.
        }
      }
      rafId = requestAnimationFrame(drawLoop)

      // Cleanup helper for delay buffer
      const cleanupDelayBuf = () => {
        for (const f of delayBuf) { try { f.bmp.close() } catch {} }
        delayBuf.length = 0
      }

      videoPipelineRef.current = { rawClone, video, canvas, previewCanvas, rafId, canvasStream, previewStream, cleanupDelayBuf }
      setActualRes(`${dim.width}x${dim.height}@${videoSendFps}fps`)
      const ua = navigator.userAgent
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in (globalThis as Record<string, unknown>))
      console.log(`[video-pipeline] Active: ${dim.width}x${dim.height} @ ${videoSendFps}fps, avDelay=${delayFrames} frames (${avSyncDelayMs}ms)`)
      logEvt('video.pipeline.start', {
        width: dim.width, height: dim.height, fps: videoSendFps,
        avDelayMs: avSyncDelayMs, avDelayFrames: delayFrames,
        ua, isIOS,
        // Track state right after setup: on iPad a broken captureStream
        // shows up as readyState='ended' or muted=true almost immediately.
        previewTrackReadyState: previewTrack.readyState,
        previewTrackMuted: previewTrack.muted,
        canvasTrackReadyState: canvasTrack.readyState,
        canvasTrackMuted: canvasTrack.muted,
      })
    }, 1200) // wait for LiveKit to finish publishing / switching device

    return () => clearTimeout(timeoutId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCamOn, videoSendRes, videoSendFps, camDeviceVersion, avSyncDelayMs])

  // ── Bitrate stats polling (send + recv) ──
  const prevStatsRef = useRef<{ sendBytes: number; recvBytes: number; ts: number } | null>(null)
  const statsTickCountRef = useRef(0)
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        // Send bitrate: camera track stats
        const camPub = localParticipant.getTrackPublication(Track.Source.Camera)
        const sender = (camPub as any)?.track?.sender as RTCRtpSender | undefined
        // Recv bitrate: sum of all remote video tracks
        let totalRecvBytes = 0
        let totalSendBytes = 0
        let outboundCount = 0
        let inboundCount = 0

        // Use room's peer connection for stats
        const pc = (room as any)?.engine?.pcManager?.publisher?.getStats?.()
        const pcSub = (room as any)?.engine?.pcManager?.subscriber?.getStats?.()

        if (pc) {
          const stats: RTCStatsReport = await pc
          stats.forEach((s: any) => {
            if (s.type === 'outbound-rtp') {
              outboundCount++
              totalSendBytes += s.bytesSent || 0
            }
          })
        } else if (sender?.getStats) {
          const stats = await sender.getStats()
          stats.forEach((s: any) => {
            if (s.type === 'outbound-rtp') { outboundCount++; totalSendBytes += s.bytesSent || 0 }
          })
        }

        if (pcSub) {
          const stats: RTCStatsReport = await pcSub
          stats.forEach((s: any) => {
            if (s.type === 'inbound-rtp') {
              inboundCount++
              totalRecvBytes += s.bytesReceived || 0
            }
          })
        }

        const prev = prevStatsRef.current
        const now = Date.now()
        if (prev && now > prev.ts) {
          const dtSec = (now - prev.ts) / 1000
          const sendDelta = Math.max(0, totalSendBytes - prev.sendBytes)
          const recvDelta = Math.max(0, totalRecvBytes - prev.recvBytes)
          setSendBitrateKbps(Math.round(sendDelta * 8 / 1000 / dtSec))
          setRecvBitrateKbps(Math.round(recvDelta * 8 / 1000 / dtSec))
        }
        prevStatsRef.current = { sendBytes: totalSendBytes, recvBytes: totalRecvBytes, ts: now }

        statsTickCountRef.current++
        if (statsTickCountRef.current % 10 === 0) {
          logEvt('stats.tick', {
            hasPcManager: !!(room as any)?.engine?.pcManager,
            hasPublisher: !!(room as any)?.engine?.pcManager?.publisher,
            hasSubscriber: !!(room as any)?.engine?.pcManager?.subscriber,
            outboundCount,
            inboundCount,
            totalSendBytes,
            totalRecvBytes,
            sendKbps: sendBitrateKbps,
            recvKbps: recvBitrateKbps,
          })
        }

        // ── Auto bandwidth adaptation (only in 'auto' tier) ──
        // Hysteresis: downgrade at BW_MED_THRESH (350), upgrade at BW_UPGRADE_THRESH (600)
        // Prevents oscillation when BW is marginal for 3 layers
        if (videoSendRes === 'auto' && prev && now > prev.ts) {
          const sendKbps = totalSendBytes > 0 ? Math.round((totalSendBytes - prev.sendBytes) * 8 / 1000 / ((now - prev.ts) / 1000)) : 0
          const db = bwDebounceRef.current
          if (sendKbps > 0 && sendKbps < BW_LOW_THRESH) {
            db.lowCount++; db.medCount++; db.okCount = 0
          } else if (sendKbps > 0 && sendKbps < BW_MED_THRESH) {
            db.lowCount = 0; db.medCount++; db.okCount = 0
          } else if (sendKbps >= BW_UPGRADE_THRESH) {
            // Only count as "ok" if BW is well above the downgrade threshold (hysteresis)
            db.lowCount = 0; db.medCount = 0; db.okCount = (db.okCount || 0) + 1
          } else {
            // Between MED_THRESH and UPGRADE_THRESH: stable zone, don't change anything
            db.lowCount = 0; db.medCount = 0
            // Don't increment okCount — not high enough to justify upgrade
          }
          const prev3 = effectiveLayersRef.current
          let next = prev3
          if (db.lowCount >= BW_DOWN_DEBOUNCE) next = 1
          else if (db.medCount >= BW_DOWN_DEBOUNCE) next = 2
          else if ((db.okCount || 0) >= BW_UP_DEBOUNCE) next = 3
          if (next !== prev3) {
            logEvt('video.bw.auto', { sendKbps, from: prev3, to: next })
            effectiveLayersRef.current = next
            setEffectiveLayers(next)
            // Reset all counters after a transition to prevent immediate re-trigger
            db.lowCount = 0; db.medCount = 0; db.okCount = 0
          }
        }
      } catch (err) {
        if (statsTickCountRef.current % 10 === 0) {
          logEvt('stats.error', { message: String(err).slice(0, 200) })
        }
      }
    }, 2000)
    return () => clearInterval(id)
  }, [room, localParticipant, videoSendRes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recording
  const [recMode, setRecMode] = useState<'off' | 'video' | 'audio'>('off')
  const [recLoading, setRecLoading] = useState(false)
  const [recStartTime, setRecStartTime] = useState<number | null>(null)
  const [recElapsed, setRecElapsed] = useState(0)

  const toggleRec = useCallback(async (mode: 'video' | 'audio') => {
    if (!roomId || recLoading) return
    setRecLoading(true)
    try {
      if (recMode !== 'off') {
        // Stop current recording first
        const stopRes = await apiFetch(`/api/visio/rooms/${roomId}/recording/stop`, { method: 'POST' })
        if (!stopRes.ok) {
          console.warn('[recording] stop:', await stopRes.text())
        }
        logEvt('recording.stopped', { mode: recMode })
        // If clicking the same mode, just stop
        if (recMode === mode) {
          setRecMode('off')
          setRecStartTime(null)
          return
        }
      }
      // Start new recording
      const res = await apiFetch(`/api/visio/rooms/${roomId}/recording/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layout: 'grid',
          width: 1280,
          height: 720,
          framerate: 30,
          mode,
        }),
      })
      if (!res.ok) {
        console.warn('[recording]', await res.text())
        setRecMode('off')
        setRecStartTime(null)
      } else {
        setRecMode(mode)
        setRecStartTime(Date.now())
        logEvt('recording.started', { mode })
      }
    } catch (err) {
      console.warn('[recording] Error:', err)
      logEvt('error.recording', { error: String(err) })
    } finally {
      setRecLoading(false)
    }
  }, [roomId, recMode, recLoading, logEvt])

  // REC timer
  useEffect(() => {
    if (!recStartTime) { setRecElapsed(0); return }
    const id = setInterval(() => setRecElapsed(Math.floor((Date.now() - recStartTime) / 1000)), 1000)
    return () => clearInterval(id)
  }, [recStartTime])

  // Warn user before leaving during active recording (BUG 6)
  // Also auto-stop recording on unmount to prevent truncation (BUG 3)
  const recModeRef = useRef(recMode)
  recModeRef.current = recMode
  useEffect(() => {
    if (recMode === 'off') return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [recMode])
  useEffect(() => {
    return () => {
      if (recModeRef.current !== 'off' && roomId) {
        apiFetch(`/api/visio/rooms/${roomId}/recording/stop`, { method: 'POST' }).catch((e) => console.warn('[recording] unmount stop failed:', e))
      }
    }
  }, [roomId])

  // Picture-in-Picture — target active remote speaker
  const [pipActive, setPipActive] = useState(false)
  const [pipFallback, setPipFallback] = useState(false) // Firefox MiniPlayer fallback

  // Find active remote speaker for PiP targeting
  const activeSpeaker = participants.find(p => p.isSpeaking && !p.isLocal)
    || participants.find(p => !p.isLocal) // fallback: first remote

  const togglePip = useCallback(async () => {
    if (pipActive) {
      if (document.pictureInPictureElement) await document.exitPictureInPicture()
      setPipActive(false)
      setPipFallback(false)
      logEvt('pip.off')
      return
    }
    const container = videoAreaRef.current
    if (!container) return

    // Find the active speaker's video element by data-participant-identity
    let video: HTMLVideoElement | null = null
    if (activeSpeaker) {
      const tile = container.querySelector(`[data-lk-participant-identity="${activeSpeaker.identity}"]`) as HTMLElement | null
      if (tile) video = tile.querySelector('video') as HTMLVideoElement | null
    }
    // Fallback: any remote video, then any video
    if (!video) {
      const allVideos = Array.from(container.querySelectorAll('video'))
      video = allVideos.find(v => {
        const tile = v.closest('[data-lk-participant-identity]')
        const id = tile?.getAttribute('data-lk-participant-identity')
        return id && id !== localParticipant.identity
      }) || allVideos[0] || null
    }
    if (!video) return

    // Check native PiP support (not available in Firefox)
    if (!document.pictureInPictureEnabled) {
      setPipFallback(true)
      setPipActive(true)
      logEvt('pip.fallback')
      return
    }

    try {
      // Remove mirror transform for PIP — LiveKit applies scaleX(-1) via CSS class on video or parent
      const saved: { el: HTMLElement; orig: string }[] = []
      let el: HTMLElement | null = video
      while (el && el !== container) {
        const computed = getComputedStyle(el).transform
        if (computed && computed !== 'none' && computed.includes('-1')) {
          saved.push({ el, orig: el.style.transform })
          el.style.transform = 'none'
        }
        el = el.parentElement
      }
      await video.requestPictureInPicture()
      setPipActive(true)
      logEvt('pip.on')
      video.addEventListener('leavepictureinpicture', () => {
        saved.forEach(s => { s.el.style.transform = s.orig })
        setPipActive(false)
        logEvt('pip.off')
      }, { once: true })
    } catch (err) { console.warn('[pip] Failed:', err) }
  }, [pipActive, logEvt, activeSpeaker, localParticipant.identity])

  // Notify parent of PiP state changes
  useEffect(() => { onPipChange?.(pipActive) }, [pipActive, onPipChange])

  // Report state to parent for compact/floating modes
  useEffect(() => {
    if (!onStateReport) return
    const speaker = participants.find(p => p.isSpeaking && !p.isLocal)
    const speakerTrack = speaker?.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack || null
    onStateReport({
      micOn: isMicOn,
      camOn: isCamOn,
      participants: participants.length,
      speakerName: speaker?.name || speaker?.identity || localParticipant.name || '',
      speakerTrack,
    })
  }, [isMicOn, isCamOn, participants.length, onStateReport]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for external toggle events (compact/floating bar controls)
  useEffect(() => {
    const onToggleMic = () => {
      localParticipant.setMicrophoneEnabled(!localParticipant.isMicrophoneEnabled)
    }
    const onToggleCam = () => {
      localParticipant.setCameraEnabled(!localParticipant.isCameraEnabled)
    }
    window.addEventListener('visio:toggle-mic', onToggleMic)
    window.addEventListener('visio:toggle-cam', onToggleCam)
    return () => {
      window.removeEventListener('visio:toggle-mic', onToggleMic)
      window.removeEventListener('visio:toggle-cam', onToggleCam)
    }
  }, [localParticipant])

  // Subtitles (real-time STT)
  const { lines: subtitleLines, partial: subtitlePartial, active: sttActive, loading: sttLoading, lang: sttLang, start: sttStart, stop: sttStop, switchLang: sttSwitchLang } = useSubtitles(room, roomId)

  // Broadcast STT captions via DataChannel + feed local overlay
  const lastBroadcastRef = useRef<{ id: string; text: string }>({ id: '', text: '' })
  useEffect(() => {
    if (!sttActive || !showCaptionOverlay || !room) return
    const encoder = new TextEncoder()
    const identity = localParticipant.identity || 'Moi'

    // Broadcast partial
    if (subtitlePartial) {
      const caption: CaptionMessage = { type: 'caption', from: identity, text: subtitlePartial.text, lang: subtitlePartial.lang, final: false }
      if (lastBroadcastRef.current.id !== subtitlePartial.id || lastBroadcastRef.current.text !== subtitlePartial.text) {
        lastBroadcastRef.current = { id: subtitlePartial.id, text: subtitlePartial.text }
        try { room.localParticipant.publishData(encoder.encode(JSON.stringify(caption)), { reliable: false }) } catch {}
      }
      // Local feed
      setCaptionEntries(prev => {
        const without = prev.filter(c => c.id !== `local-partial`)
        return [...without, { id: `local-partial`, from: identity, text: subtitlePartial.text, final: false, ts: Date.now() }].slice(-10)
      })
    }

    // Broadcast finals
    if (subtitleLines.length > 0) {
      const last = subtitleLines[subtitleLines.length - 1]
      if (last.isFinal) {
        const caption: CaptionMessage = { type: 'caption', from: identity, text: last.text, lang: last.lang, final: true }
        try { room.localParticipant.publishData(encoder.encode(JSON.stringify(caption)), { reliable: true }) } catch {}
        setCaptionEntries(prev => {
          const without = prev.filter(c => c.id !== `local-partial`)
          captionIdRef.current++
          return [...without, { id: `local-${captionIdRef.current}`, from: identity, text: last.text, final: true, ts: Date.now() }].slice(-10)
        })
      }
    }
  }, [subtitleLines, subtitlePartial, sttActive, showCaptionOverlay, room, localParticipant.identity])

  // Auto-clean old caption entries every second
  useEffect(() => {
    if (!showCaptionOverlay) return
    const id = setInterval(() => {
      setCaptionEntries(prev => {
        const cutoff = Date.now() - 6000
        const filtered = prev.filter(c => c.ts > cutoff)
        return filtered.length !== prev.length ? filtered : prev
      })
    }, 1000)
    return () => clearInterval(id)
  }, [showCaptionOverlay])

  const toggleMenu = (menu: string) => {
    setOpenMenu(prev => prev === menu ? null : menu)
  }
  const closeMenus = () => setOpenMenu(null)

  // Keyboard shortcuts
  const pttMicWasOn = useRef(false)
  useKeyboardShortcuts({
    toggleMic: () => {
      if (!isMicOn && !perms.can_unmute_audio) return
      localParticipant.setMicrophoneEnabled(!isMicOn)
      logEvt(isMicOn ? 'mic.off' : 'mic.on')
    },
    toggleCam: () => {
      if (!isCamOn && !perms.can_unmute_video) return
      localParticipant.setCameraEnabled(!isCamOn).catch((e) => logEvt('error.camera', { error: String(e) }))
      logEvt(isCamOn ? 'camera.off' : 'camera.on')
    },
    toggleHand: () => toggleHand(),
    toggleChat: () => { setShowChat(c => !c); setShowParticipants(false); setShowStats(false); setShowWhiteboard(false) },
    toggleParticipants: () => { setShowParticipants(p => !p); setShowChat(false); setShowStats(false); setShowWhiteboard(false) },
    toggleScreenShare: () => {
      if (!perms.can_share_screen) return
      localParticipant.setScreenShareEnabled(!isScreenSharing, screenShareOpts, screenSharePublishOpts)
      logEvt(isScreenSharing ? 'screenshare.off' : 'screenshare.on')
    },
    toggleFullscreen: () => {
      if (document.fullscreenElement) document.exitFullscreen().catch((e) => logEvt('error.fullscreen', { error: String(e) }))
      else document.documentElement.requestFullscreen().catch((e) => logEvt('error.fullscreen', { error: String(e) }))
    },
    closePanels: () => {
      closeMenus()
      setShowParticipants(false); setShowChat(false); setShowStats(false); setShowWhiteboard(false)
      setShowShortcutsHelp(false)
    },
    showShortcutsHelp: () => setShowShortcutsHelp(s => !s),
    pushToTalkStart: () => {
      pttMicWasOn.current = isMicOn
      if (!isMicOn && perms.can_unmute_audio) {
        localParticipant.setMicrophoneEnabled(true)
        logEvt('ptt.start')
      }
    },
    pushToTalkEnd: () => {
      if (!pttMicWasOn.current) {
        localParticipant.setMicrophoneEnabled(false)
        logEvt('ptt.end')
      }
    },
  }, true)

  return (
    <div className="absolute inset-0 bg-zinc-950">
      {!speakerMuted && <RoomAudioRenderer />}
      {/* ── MiniPlayer fallback (Firefox / no native PiP) ── */}
      {pipFallback && createPortal(
        <MiniPlayer
          activeSpeaker={activeSpeaker}
          onBack={() => { setPipFallback(false); setPipActive(false); logEvt('pip.off') }}
          onQuit={() => { setPipFallback(false); setPipActive(false); logEvt('room.left', { source: 'mini_player' }); room.disconnect(); }}
          isMicOn={isMicOn}
          isCamOn={isCamOn}
          toggleMic={() => { localParticipant.setMicrophoneEnabled(!isMicOn); logEvt(isMicOn ? 'mic.off' : 'mic.on', { source: 'mini_player' }); }}
          toggleCam={() => { localParticipant.setCameraEnabled(!isCamOn); logEvt(isCamOn ? 'camera.off' : 'camera.on', { source: 'mini_player' }); }}
        />,
        document.body
      )}
      {/* ── Video area ────────────────────────────────── */}
      <div ref={videoAreaRef} className={`absolute inset-0 bottom-12 overflow-hidden ${sttActive ? 'right-[15%]' : ''} ${showParticipants || showStats ? 'right-72' : ''}`} onClick={closeMenus}>
        {/* REC indicator (H6) */}
        {recMode !== 'off' && (
          <div className="absolute top-3 left-3 z-50 flex items-center gap-2 bg-black/70 rounded-full px-3 py-1.5 pointer-events-none">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-white text-xs font-mono font-bold tracking-wider">
              REC {String(Math.floor(recElapsed / 60)).padStart(2, '0')}:{String(recElapsed % 60).padStart(2, '0')}
            </span>
            <span className="text-zinc-400 text-[10px] uppercase">{recMode}</span>
          </div>
        )}
        {(() => {
          // Deduplicate tracks by participant identity (H1 — prevent ghost tiles)
          const seen = new Set<string>()
          const uniqueCameraTracks = cameraTracks.filter(t => {
            const id = t.participant?.identity || ''
            if (!id || seen.has(id)) return false
            seen.add(id)
            return true
          })
          const displayTracks = hideNoVideo
            ? uniqueCameraTracks.filter(t => t.participant?.isCameraEnabled || t.participant?.isLocal)
            : uniqueCameraTracks

          // Focus mode: whiteboard in center
          if (showWhiteboard && layoutMode === 'focus') {
            return (
              <div className="flex h-full w-full">
                <div className="w-48 shrink-0 flex flex-col gap-1 p-1 overflow-y-auto bg-zinc-950">
                  {displayTracks.map((t, i) => (
                    <div key={t.participant?.identity || i} className="w-full aspect-video rounded overflow-hidden">
                      <ParticipantTile trackRef={t} />
                    </div>
                  ))}
                </div>
                <div className="flex-1 relative">
                  <Whiteboard embedded externalProvider={whiteboardProvider} onClose={() => {
                    setShowWhiteboard(false)
                    try {
                      const encoder = new TextEncoder()
                      const msg = encoder.encode(JSON.stringify({ type: 'whiteboard_off' }))
                      room.localParticipant.publishData(msg, { reliable: true })
                    } catch {}
                  }} roomId={roomId!} />
                </div>
              </div>
            )
          }

          if (screenShareTracks.length > 0) {
            const screenSharerIdentity = screenShareTracks[screenShareTracks.length - 1].participant?.identity
            const isLocalScreenShare = screenSharerIdentity === localParticipant.identity
            return (
              <FocusLayoutContainer>
                <CarouselLayout tracks={displayTracks}>
                  <HandRaisedTile />
                </CarouselLayout>
                <div className="relative flex-1">
                  <FocusLayout trackRef={screenShareTracks[screenShareTracks.length - 1]} />
                  <AnnotationOverlay
                    strokes={annot.strokes}
                    onStrokeComplete={annot.addStroke}
                    onUndo={annot.undoLast}
                    onRemoveStroke={annot.removeStroke}
                    onClear={annot.clearAll}
                    enabled={annot.annotationsEnabled}
                    isSharer={isLocalScreenShare}
                    onToggle={isLocalScreenShare ? annot.toggleAnnotations : undefined}
                  />
                </div>
              </FocusLayoutContainer>
            )
          }
          if (layoutMode === 'focus') {
            return (
              <FocusLayoutContainer>
                <CarouselLayout tracks={displayTracks}>
                  <HandRaisedTile />
                </CarouselLayout>
                {displayTracks[0] && <FocusLayout trackRef={displayTracks[0]} />}
              </FocusLayoutContainer>
            )
          }
          return (
            <GridLayout key={layoutKey} tracks={displayTracks}>
              <HandRaisedTile />
            </GridLayout>
          )
        })()}

        {/* Reaction bubbles on video area */}
        {activeReactions.map(r => (
          <ReactionBubble key={r.id} emoji={r.emoji} />
        ))}

        {/* Mute notification */}
        {muteNotification && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-red-600/90 text-white rounded-lg text-sm shadow-lg">
            {muteNotification}
          </div>
        )}

        {isScreenSharing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50">
            <button
              onClick={() => { localParticipant.setScreenShareEnabled(false, screenShareOpts, screenSharePublishOpts); logEvt('screenshare.off', { source: 'share_banner' }) }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium shadow-lg flex items-center gap-2"
            >
              <ScreenOffIcon />
              Arreter le partage
            </button>
          </div>
        )}
      </div>

      {/* ── Side panels ──────────────────────────────── */}
      {showParticipants && roomId && (
        <ParticipantList roomId={roomId} isAdmin={isAdmin} onClose={() => setShowParticipants(false)} />
      )}
      {showStats && (
        <StatsPanel
          onClose={() => setShowStats(false)}
        />
      )}
      {showChat && (
        <VisioDataChat onClose={() => setShowChat(false)} channelId={channelId} isGuest={isGuest} />
      )}
      {showNotes && (
        <MeetingNotes
          roomId={roomId}
          isHost={!isGuest}
          isAdmin={isAdmin}
          onClose={() => setShowNotes(false)}
        />
      )}
      {showWhiteboard && layoutMode !== 'focus' && (
        <Whiteboard onClose={() => {
          setShowWhiteboard(false)
          try {
            const encoder = new TextEncoder()
            const msg = encoder.encode(JSON.stringify({ type: 'whiteboard_off' }))
            room.localParticipant.publishData(msg, { reliable: true })
          } catch {}
        }} roomId={roomId!} />
      )}

      {/* ── Breakout indicator ── */}
      {breakoutActive && (
        <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center gap-3 py-1.5 bg-yellow-900/80 border-b border-yellow-700">
          <span className="text-yellow-300 text-sm font-medium">Sous-salle {breakoutRoomN}</span>
          {timerSec !== null && timerSec > 0 && (
            <span className="text-yellow-400 text-xs font-mono">
              {Math.floor(timerSec / 60)}:{String(timerSec % 60).padStart(2, '0')}
            </span>
          )}
          {isAdmin && (
            <button onClick={recallBreakout} className="px-2 py-0.5 bg-yellow-700 hover:bg-yellow-600 text-yellow-100 text-xs rounded">
              Rassembler
            </button>
          )}
        </div>
      )}

      {/* ── BreakoutSetup modal ── */}
      {showBreakoutSetup && roomId && (
        <BreakoutSetup
          roomId={roomId}
          participants={participants.map(p => ({ identity: p.identity, name: p.name || p.identity }))}
          onStart={startBreakout}
          onClose={() => setShowBreakoutSetup(false)}
        />
      )}

      {/* ── Caption overlay (on video area) ── */}
      {showCaptionOverlay && <CaptionOverlay captions={captionEntries} size={captionSize} />}

      {/* ── Subtitle bar (between video and controls) ── */}
      {sttActive && <SubtitleBar lines={subtitleLines} partial={subtitlePartial} lang={sttLang} onLangChange={sttSwitchLang} />}

      {/* ── Control bar ───────────────────────────────── */}
      <div className="absolute bottom-0 left-0 right-0 h-12 flex items-center justify-center gap-1 px-2 bg-zinc-900 border-t border-zinc-800">

        {/* ── 1. Enceinte (speaker) ── */}
        <EqBadge gains={larsenStatus.gains} bands={larsenStatus.bands} />
        <div className="relative flex">
          <button
            onClick={() => { setSpeakerMuted(m => { logEvt(m ? 'speaker.unmuted' : 'speaker.muted'); return !m }); }}
            className={`flex items-center gap-1 pl-2 pr-1 py-2 rounded-l text-xs font-medium transition-colors ${
              speakerMuted ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
          >
            <SpeakerIcon muted={speakerMuted} />
            Enceinte
          </button>
          <button
            onClick={() => toggleMenu('speaker')}
            className={`px-1 py-2 rounded-r border-l border-zinc-600 text-xs transition-colors ${
              openMenu === 'speaker' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
          >
            <ChevronIcon />
          </button>
          {openMenu === 'speaker' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Enceinte / Sortie audio</MenuTitle>
              <DeviceSelector kind="audiooutput" label="Sortie" onSwitch={(k, d) => logEvt('device.switch', { kind: k, device: d })} onError={(k, e) => logEvt('device.error', { kind: k, error: e })} />
              <div className="mt-2 pt-2 border-t border-zinc-700">
                <div className="flex gap-1 mb-2">
                  <ModeBtn active={speakerMode === 'antilarsen'} onClick={() => { setSpeakerMode('antilarsen'); logEvt('speaker.mode', { mode: 'antilarsen' }) }}>Anti-Larsen</ModeBtn>
                  <ModeBtn active={speakerMode === 'manual'} onClick={() => { setSpeakerMode('manual'); logEvt('speaker.mode', { mode: 'manual' }) }}>Manuel</ModeBtn>
                </div>
                {speakerMode === 'antilarsen' && (
                  <div className="text-[10px] text-zinc-500 mb-2">Limiteur 7 bandes automatique actif</div>
                )}
                {speakerMode === 'manual' && (
                  <VolumeSlider value={speakerLevel} onChange={(v: number) => { setSpeakerLevel(v); logEvt('speaker.level', { level: v }) }} label="Volume enceinte" />
                )}
                {/* EQ 7-band display */}
                <EqDisplay gains={larsenStatus.gains} bands={larsenStatus.bands} />
              </div>
            </MenuPanel>
          )}
        </div>

        <Separator />

        {/* ── 2. Micro ── */}
        <div className="relative flex">
          <button
            onClick={() => { if (!isMicOn && !perms.can_unmute_audio) return; localParticipant.setMicrophoneEnabled(!isMicOn); logEvt(isMicOn ? 'mic.off' : 'mic.on') }}
            disabled={!perms.can_unmute_audio && !isMicOn}
            className={`flex items-center gap-1 pl-2 pr-1 py-2 rounded-l text-xs font-medium transition-colors ${
              isMicOn ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-red-600 text-white hover:bg-red-500'
            } ${!perms.can_unmute_audio && !isMicOn ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <MicIcon muted={!isMicOn} />
            <span title="Alt+M">Micro</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleMenu('mic') }}
            className={`px-1 py-2 rounded-r border-l text-xs transition-colors ${
              isMicOn ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600' : 'bg-red-600 text-white hover:bg-red-500 border-red-500'
            }`}
          >
            <ChevronIcon />
          </button>
          {openMenu === 'mic' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Microphone</MenuTitle>
              <div className="text-[10px] text-zinc-400 mb-2 pb-2 border-b border-zinc-700">
                Delai video applique : <span className="text-cyan-400 font-mono">{avSyncDelayMs} ms</span>
                <span className="text-zinc-500"> ({denoiser.mode === 'off'
                  ? 'DN off'
                  : avSyncMode === 'fast1' ? 'DN Fast1'
                  : avSyncMode === 'sync'  ? 'DN Fast2'
                  : 'DN Quality'}{benchProfile && avSyncMode !== 'fast1' ? ', bench' : ', defaut'})</span>
              </div>
              <DeviceSelector kind="audioinput" label="Micro" onSwitch={(k, d) => logEvt('device.switch', { kind: k, device: d })} onError={(k, e) => logEvt('device.error', { kind: k, error: e })} />
              <div className="mt-2 pt-2 border-t border-zinc-700">
                <div className="flex gap-1 mb-2">
                  <ModeBtn active={micMode === 'auto'} onClick={() => { setMicMode('auto'); logEvt('mic.mode', { mode: 'auto' }) }}>Auto (AGC)</ModeBtn>
                  <ModeBtn active={micMode === 'manual'} onClick={() => { setMicMode('manual'); logEvt('mic.mode', { mode: 'manual' }) }}>Manuel</ModeBtn>
                </div>
                {micMode === 'manual' && (
                  <VolumeSlider value={micLevel} onChange={(v: number) => { setMicLevel(v); logEvt('mic.level', { level: v }) }} label="Gain micro" />
                )}
                {micMode === 'auto' && (
                  <div className="text-[10px] text-zinc-500">Gain automatique (AGC) actif</div>
                )}
                {/* Noise suppression (Moment DeepFilterNet3) + A/V sync combined */}
                <div className="mt-2 pt-2 border-t border-zinc-700">
                  <div className="text-[10px] text-zinc-500 mb-1">Suppression de bruit (Moment AI)</div>
                  <div className="flex gap-1">
                    <ModeBtn active={denoiser.mode === 'off'} onClick={() => {
                      denoiser.setMode('off')
                      logEvt('denoise.mode', { mode: 'off' })
                    }}>Off</ModeBtn>
                    <ModeBtn active={denoiser.mode === 'on' && avSyncMode === 'fast1'} onClick={() => {
                      denoiser.setMode('on')
                      setAvSyncMode('fast1'); denoiseEngine.setPreset('fast1')
                      logEvt('denoise.mode', { mode: 'on', avSync: 'fast1' })
                    }}>Fast1</ModeBtn>
                    <ModeBtn active={denoiser.mode === 'on' && avSyncMode === 'sync'} onClick={() => {
                      denoiser.setMode('on')
                      setAvSyncMode('sync'); denoiseEngine.setPreset('sync')
                      logEvt('denoise.mode', { mode: 'on', avSync: 'sync' })
                    }}>Fast2</ModeBtn>
                    <ModeBtn active={denoiser.mode === 'on' && avSyncMode === 'async'} onClick={() => {
                      denoiser.setMode('on')
                      setAvSyncMode('async'); denoiseEngine.setPreset('async')
                      logEvt('denoise.mode', { mode: 'on', avSync: 'async' })
                    }}>Quality</ModeBtn>
                  </div>
                  <div className="text-[9px] text-zinc-600 mt-0.5">
                    Delai video : {avSyncDelayMs} ms
                  </div>
                  {denoiser.isLoading && (
                    <div className="text-[10px] text-zinc-500 mt-1">Chargement du modele...</div>
                  )}
                  {denoiser.error && (
                    <div className="text-[10px] text-red-400 mt-1">{denoiser.error}</div>
                  )}
                </div>
                {isMicOn && (
                  <>
                    <div className="mt-1.5">
                      <div className="text-[10px] text-zinc-500 mb-0.5">Niveau</div>
                      <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${micVu > 80 ? 'bg-red-500' : micVu > 50 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                          style={{ width: `${micVu}%` }}
                        />
                      </div>
                    </div>
                    <EqDisplay gains={[]} bands={micBands} title="Spectre micro 7 bandes" />
                  </>
                )}
              </div>
            </MenuPanel>
          )}
        </div>
        {/* Noise suppression indicator (filter icon) */}
        {denoiser.isActive && isMicOn && (
          <span className="text-cyan-400 text-xs ml-[-4px]" title="Suppression de bruit active">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
          </span>
        )}

        {/* ── 3. Son distant ── */}
        <div className="relative flex">
          <button
            onClick={() => toggleMenu('remote')}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              openMenu === 'remote' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
          >
            <VolumeIcon />
            Distant
            <ChevronIcon />
          </button>
          {openMenu === 'remote' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Son distant (entrant)</MenuTitle>
              <div className="flex gap-1 mb-2">
                <ModeBtn active={remoteMode === 'auto'} onClick={() => { setRemoteMode('auto'); logEvt('remote.mode', { mode: 'auto' }) }}>Auto</ModeBtn>
                <ModeBtn active={remoteMode === 'manual'} onClick={() => { setRemoteMode('manual'); logEvt('remote.mode', { mode: 'manual' }) }}>Manuel</ModeBtn>
              </div>
              {remoteMode === 'manual' && (
                <VolumeSlider value={remoteLevel} onChange={(v: number) => { setRemoteLevel(v); logEvt('remote.level', { level: v }) }} label="Volume distant" />
              )}
              {remoteMode === 'auto' && (
                <div className="text-[10px] text-zinc-500">Volume natif du navigateur</div>
              )}
              {/* Remote audio level from anti-larsen bands */}
              <div className="mt-2 pt-2 border-t border-zinc-700">
                <div className="text-[10px] text-zinc-500 mb-1">Niveau entrant</div>
                <RemoteVuMeter bands={larsenStatus.bands} />
              </div>
              <EqDisplay gains={[]} bands={larsenStatus.bands} title="Spectre distant 7 bandes" />
            </MenuPanel>
          )}
        </div>

        {/* ── BUF indicator (QoS audio buffer) ── */}
        <BufIndicator bufferSeconds={qosStatus.bufferSeconds} buffering={qosStatus.buffering} catchingUp={qosStatus.catchingUp} />

        <Separator />

        {/* ── 4. Video envoi ── */}
        <div className="relative flex">
          <button
            onClick={async () => {
              if (!isCamOn && !perms.can_unmute_video) return
              try {
                await localParticipant.setCameraEnabled(!isCamOn)
                logEvt(isCamOn ? 'camera.off' : 'camera.on')
              } catch (err) {
                console.error('[VisioRoom] setCameraEnabled failed:', err)
                logEvt('camera.error', { error: String(err) })
              }
            }}
            disabled={!perms.can_unmute_video && !isCamOn}
            className={`flex items-center gap-1 pl-2 pr-1 py-2 rounded-l text-xs font-medium transition-colors ${
              isCamOn ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-red-600 text-white hover:bg-red-500'
            } ${!perms.can_unmute_video && !isCamOn ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <CamIcon muted={!isCamOn} />
            <span title="Alt+V">Envoi</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleMenu('videosend') }}
            className={`px-1 py-2 rounded-r border-l text-xs transition-colors ${
              isCamOn ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600' : 'bg-red-600 text-white hover:bg-red-500 border-red-500'
            }`}
          >
            <ChevronIcon />
          </button>
          {openMenu === 'videosend' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Video envoi</MenuTitle>
              <DeviceSelector kind="videoinput" label="Camera" onDeviceChange={() => setCamDeviceVersion(v => v + 1)} onSwitch={(k, d) => logEvt('device.switch', { kind: k, device: d })} onError={(k, e) => logEvt('device.error', { kind: k, error: e })} />
              <div className="mt-2 pt-2 border-t border-zinc-700">
                <div className="text-[9px] text-zinc-500 mb-1">Resolution</div>
                <div className="flex gap-0.5 mb-2">
                  {VIDEO_TIERS.map(t => (
                    <ModeBtn key={t.key} active={videoSendRes === t.key} onClick={() => { setVideoSendRes(t.key); logEvt('video.res', { res: t.key }) }}>{t.label}</ModeBtn>
                  ))}
                </div>
                {videoSendRes === 'auto' && (
                  <div className="text-[10px] text-zinc-500 mb-1">Adapte les couches simulcast selon le debit montant</div>
                )}
                <div className="text-[9px] text-zinc-500 mb-1">FPS</div>
                <div className="flex gap-0.5 mb-2">
                  {FPS_OPTIONS.map(f => (
                    <ModeBtn key={f} active={videoSendFps === f} onClick={() => { setVideoSendFps(f); logEvt('video.fps', { fps: f }) }}>{f}</ModeBtn>
                  ))}
                </div>
                <div className="text-[9px] text-zinc-500 mb-1">Audio bitrate</div>
                <div className="flex gap-0.5 mb-2">
                  {AUDIO_TIERS.map(t => (
                    <ModeBtn key={t.key} active={audioSendMax === t.key} onClick={() => { setAudioSendMax(t.key); logEvt('audio.bitrate', { bitrate: t.key }) }}>{t.label}k</ModeBtn>
                  ))}
                </div>
              </div>
              {/* Real-time indicator */}
              <div className="mt-2 pt-2 border-t border-zinc-700 text-[9px] text-zinc-400 space-y-0.5">
                <div>Resolution : <span className="text-emerald-400 font-mono">{actualRes || `${VIDEO_SEND_DIMS[videoSendRes].width}x${VIDEO_SEND_DIMS[videoSendRes].height}@${videoSendFps}fps`}</span></div>
                <div>Debit : <span className="text-emerald-400 font-mono">{sendBitrateKbps > 0 ? `${sendBitrateKbps} kbps` : '--'}</span></div>
                <div>Couches : <span className={`font-mono ${effectiveLayers >= 3 ? 'text-emerald-400' : effectiveLayers === 2 ? 'text-yellow-400' : 'text-red-400'}`}>{effectiveLayers}/{videoSendRes === '180p' ? 1 : videoSendRes === '360p' ? 2 : 3}</span></div>
              </div>
            </MenuPanel>
          )}
        </div>

        {/* ── 5. Video reception ── */}
        <div className="relative">
          <button
            onClick={() => toggleMenu('videorecv')}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              openMenu === 'videorecv' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
          >
            <VideoRecvIcon />
            Recep
            <ChevronIcon />
          </button>
          {openMenu === 'videorecv' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Video reception</MenuTitle>
              <div className="text-[9px] text-zinc-500 mb-1">Qualite</div>
              <div className="flex gap-0.5 mb-2">
                {VIDEO_RECV_OPTIONS.map(o => (
                  <ModeBtn key={o.key} active={videoRecvForce === o.key} onClick={() => { setVideoRecvForce(o.key); logEvt('video.recv', { quality: o.key }) }}>{o.label}</ModeBtn>
                ))}
              </div>
              {videoRecvForce === 'auto' && (
                <div className="text-[10px] text-zinc-500 mb-2">Le SFU adapte la qualite selon la bande passante</div>
              )}
              <div className="text-[9px] text-zinc-500 mb-1">Audio reception</div>
              <div className="flex gap-0.5 mb-2">
                {AUDIO_TIERS.map(t => (
                  <ModeBtn key={t.key} active={audioRecvForce === t.key} onClick={() => { setAudioRecvForce(t.key); logEvt('audio.recv', { quality: t.key }) }}>{t.label}k</ModeBtn>
                ))}
              </div>
              {/* Real-time indicator */}
              <div className="mt-2 pt-2 border-t border-zinc-700 text-[9px] text-zinc-400 space-y-0.5">
                <div>Debit recu : <span className="text-emerald-400 font-mono">{recvBitrateKbps > 0 ? `${recvBitrateKbps} kbps` : '--'}</span></div>
              </div>
            </MenuPanel>
          )}
        </div>

        <Separator />

        {/* ── Partager ecran ── */}
        {perms.can_share_screen && (
        <button
          onClick={() => { localParticipant.setScreenShareEnabled(!isScreenSharing, screenShareOpts, screenSharePublishOpts); logEvt(isScreenSharing ? 'screenshare.off' : 'screenshare.on') }}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            isScreenSharing ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-zinc-700 text-white hover:bg-zinc-600'
          }`}
        >
          {isScreenSharing ? <ScreenOffIcon /> : <ScreenIcon />}
          <span title="Alt+S">Ecran</span>
        </button>
        )}

        {/* Record Video (host only) */}
        {!isGuest && (
        <button
          onClick={() => toggleRec('video')}
          disabled={recLoading}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            recMode === 'video' ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          } ${recLoading ? 'opacity-50' : ''}`}
          title={recMode === 'video' ? 'Arreter l\'enregistrement video' : 'Enregistrer video + audio'}
        >
          <RecIcon recording={recMode === 'video'} />
          Rec Vid
        </button>
        )}

        {/* Record Audio (host only) */}
        {!isGuest && (
        <button
          onClick={() => toggleRec('audio')}
          disabled={recLoading}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            recMode === 'audio' ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          } ${recLoading ? 'opacity-50' : ''}`}
          title={recMode === 'audio' ? 'Arreter l\'enregistrement audio' : 'Enregistrer audio seulement'}
        >
          <RecIcon recording={recMode === 'audio'} />
          Rec Aud
        </button>
        )}

        {/* CC (host only) — split button with submenu */}
        {!isGuest && (
        <div className="relative flex">
          <button
            onClick={() => { if (sttActive) { sttStop(); setShowCaptionOverlay(false); logEvt('cc.off') } else { sttStart(); logEvt('cc.on'); setShowParticipants(false); setShowChat(false); setShowWhiteboard(false); setShowStats(false) } }}
            disabled={sttLoading}
            className={`flex items-center gap-1 pl-2 pr-1 py-2 rounded-l text-xs font-medium transition-colors ${
              sttActive ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            } ${sttLoading ? 'opacity-50' : ''}`}
            title={sttActive ? 'Desactiver les sous-titres' : 'Activer les sous-titres'}
          >
            <CcIcon />
            CC
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleMenu('cc') }}
            className={`px-1 py-2 rounded-r border-l text-xs transition-colors ${
              sttActive ? 'bg-blue-600 text-white hover:bg-blue-500 border-blue-500' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 border-zinc-600'
            }`}
          >
            <ChevronIcon />
          </button>
          {openMenu === 'cc' && (
            <MenuPanel onClick={e => e.stopPropagation()}>
              <MenuTitle>Sous-titres</MenuTitle>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCaptionOverlay}
                    onChange={() => setShowCaptionOverlay(v => !v)}
                    className="rounded bg-zinc-700 border-zinc-600"
                  />
                  Overlay video
                </label>
                {showCaptionOverlay && (
                  <div className="ml-4">
                    <div className="text-[10px] text-zinc-500 mb-1">Taille police</div>
                    <div className="flex gap-1">
                      <ModeBtn active={captionSize === 'small'} onClick={() => { setCaptionSize('small'); try { localStorage.setItem('visio-caption-size', 'small') } catch {} }}>Petit</ModeBtn>
                      <ModeBtn active={captionSize === 'medium'} onClick={() => { setCaptionSize('medium'); try { localStorage.setItem('visio-caption-size', 'medium') } catch {} }}>Moyen</ModeBtn>
                      <ModeBtn active={captionSize === 'large'} onClick={() => { setCaptionSize('large'); try { localStorage.setItem('visio-caption-size', 'large') } catch {} }}>Grand</ModeBtn>
                    </div>
                  </div>
                )}
              </div>
            </MenuPanel>
          )}
        </div>
        )}

        {/* PiP */}
        <button
          onClick={togglePip}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            pipActive ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
          title={pipActive ? 'Fermer Picture-in-Picture' : 'Detacher la video'}
        >
          <PipIcon />
          PiP
        </button>

        <Separator />

        {/* ── E16: Lever la main ── */}
        {perms.can_raise_hand && (
        <button
          onClick={toggleHand}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            handRaised ? 'bg-yellow-500 text-black hover:bg-yellow-400' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
          title={handRaised ? 'Baisser la main (Alt+H)' : 'Lever la main (Alt+H)'}
        >
          <HandIcon />
        </button>
        )}

        {/* ── E17: Reactions ── */}
        {perms.can_react && (
        <div className="relative">
          <button
            onClick={() => toggleMenu('reactions')}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              openMenu === 'reactions' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            title="Reactions"
          >
            <ReactionIcon />
          </button>
          {openMenu === 'reactions' && (
            <ReactionPicker onReact={(emoji) => { sendReaction(emoji); logEvt('reaction', { emoji }) }} onClose={closeMenus} />
          )}
        </div>
        )}

        {/* ── E15: Participants ── */}
        <button
          onClick={() => { setShowParticipants(p => { if (!p && sttActive) sttStop(); return !p }); setShowStats(false); setShowChat(false); setShowWhiteboard(false) }}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            showParticipants ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
          title="Participants (Alt+P)"
        >
          <ParticipantsIcon />
        </button>

        {/* ── E14: Chat ── */}
        {(channelId || isGuest) && (
          <button
            onClick={() => { setShowChat(c => { if (!c && sttActive) sttStop(); return !c }); setShowParticipants(false); setShowStats(false); setShowWhiteboard(false) }}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              showChat ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            title="Chat (Alt+C)"
          >
            <ChatIcon />
          </button>
        )}

        {/* ── Focus mode toggle ── */}
        <button
          onClick={() => {
            const newMode = layoutMode === 'focus' ? 'grid' : 'focus'
            setLayoutMode(newMode)
            logEvt('layout.changed', { mode: newMode })
          }}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            layoutMode === 'focus' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
          title={layoutMode === 'focus' ? 'Mode grille' : 'Mode focus'}
        >
          <FocusIcon />
        </button>

        {/* ── M003: Whiteboard ── */}
        <button
          onClick={() => {
            const nw = !showWhiteboard
            setShowWhiteboard(nw)
            logEvt(nw ? 'whiteboard.on' : 'whiteboard.off')
            if (nw) {
              setLayoutMode('focus')
              if (sttActive) sttStop()
              setShowParticipants(false); setShowStats(false); setShowChat(false)
            }
            // Broadcast to all participants
            try {
              const encoder = new TextEncoder()
              const msg = encoder.encode(JSON.stringify({ type: nw ? 'whiteboard_on' : 'whiteboard_off' }))
              room.localParticipant.publishData(msg, { reliable: true })
            } catch (e) { console.warn('[VisioRoom] whiteboard broadcast', e) }
          }}
          className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
            showWhiteboard ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
          title="Tableau blanc"
        >
          <WhiteboardIcon />
        </button>

        {/* ── M004: Breakout ── */}
        {isAdmin && !breakoutActive && (
          <button
            onClick={() => setShowBreakoutSetup(true)}
            className="flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
            title="Sous-salles"
          >
            <BreakoutIcon />
          </button>
        )}

        {/* ── E19: Copy link ── */}
        {inviteToken && (
          <button
            onClick={copyInviteLink}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs font-medium transition-colors ${
              linkCopied ? 'bg-emerald-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            title={linkCopied ? 'Copie !' : 'Copier le lien d\'invitation'}
          >
            <LinkIcon />
          </button>
        )}

        <Separator />

        {/* ── Minimize (compact/floating) ── */}
        {onMinimize && (
          <div className="relative">
            <button
              onClick={() => toggleMenu('minimize')}
              className={`flex items-center gap-1 px-2 py-2 rounded text-xs transition-colors ${
                openMenu === 'minimize' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
              title="Reduire"
            >
              <MinimizeIcon />
            </button>
            {openMenu === 'minimize' && (
              <MenuPanel align="right" onClick={e => e.stopPropagation()}>
                <MenuTitle>Reduire la visio</MenuTitle>
                <button
                  onClick={() => { onMinimize('compact'); logEvt('layout.compact'); closeMenus() }}
                  className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 rounded flex items-center gap-2"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 shrink-0"><rect x="2" y="18" width="20" height="4" rx="1" /></svg>
                  Barre compacte
                </button>
                <button
                  onClick={() => { onMinimize('floating'); logEvt('layout.floating'); closeMenus() }}
                  className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 rounded flex items-center gap-2 mt-0.5"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 shrink-0"><rect x="10" y="10" width="12" height="10" rx="1" /><path d="M2 6h14v3" /></svg>
                  Fenetre flottante
                </button>
              </MenuPanel>
            )}
          </div>
        )}

        {/* ── E18: Layout + E21: Stats + Settings ── */}
        <div className="relative">
          <button
            onClick={() => toggleMenu('settings')}
            className={`flex items-center gap-1 px-2 py-2 rounded text-xs transition-colors ${
              openMenu === 'settings' ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            title="Parametres (raccourcis: ?)"
          >
            <SettingsIcon />
          </button>
          {openMenu === 'settings' && (
            <MenuPanel align="right" onClick={e => e.stopPropagation()}>
              <MenuTitle>Parametres</MenuTitle>
              {roomMode && (
                <div className="mb-2 px-2 py-1 bg-zinc-900 rounded text-xs text-zinc-400">
                  Mode : {roomMode === '1to1' ? '1 a 1' : 'Groupe'}
                </div>
              )}
              {/* View options */}
              <ToggleRow label="Masquer sans camera" desc="Cacher les participants camera eteinte" checked={hideNoVideo} onChange={(v: boolean) => { setHideNoVideo(v); logEvt('settings.hideNoVideo', { enabled: v }) }} />
              <ToggleRow label="Reduction de bruit" desc="Filtre les bruits de fond" checked={noiseSuppression} onChange={(v: boolean) => { setNoiseSuppression(v); logEvt('audio.noiseSuppression', { enabled: v }) }} />
              <ToggleRow label="Anti-echo" desc="Supprime les boucles audio" checked={echoCancellation} onChange={(v: boolean) => { setEchoCancellation(v); logEvt('audio.echoCancellation', { enabled: v }) }} />
              {/* E24: Room permissions (admin only) */}
              {isAdmin && (
                <div className="mt-2 pt-2 border-t border-zinc-700">
                  <div className="text-[10px] text-zinc-500 mb-1 uppercase tracking-wide">Permissions participants</div>
                  <ToggleRow label="Partage ecran" desc="Les participants peuvent partager leur ecran" checked={roomPerms.can_share_screen} onChange={() => togglePermission('can_share_screen')} />
                  <ToggleRow label="Lever la main" desc="Les participants peuvent lever la main" checked={roomPerms.can_raise_hand} onChange={() => togglePermission('can_raise_hand')} />
                  <ToggleRow label="Reactions" desc="Les participants peuvent envoyer des reactions" checked={roomPerms.can_react} onChange={() => togglePermission('can_react')} />
                  <ToggleRow label="Activer camera" desc="Les participants peuvent allumer leur camera" checked={roomPerms.can_unmute_video} onChange={() => togglePermission('can_unmute_video')} />
                  <ToggleRow label="Activer micro" desc="Les participants peuvent activer leur micro" checked={roomPerms.can_unmute_audio} onChange={() => togglePermission('can_unmute_audio')} />
                </div>
              )}
              {/* Virtual background */}
              <div className="mt-2 pt-2 border-t border-zinc-700">
                <VirtualBgMenu
                  activeId={vbg.activeId}
                  onSelect={(id: string) => { vbg.setActiveId(id); logEvt('vbg.change', { activeId: id }) }}
                  onCustomImage={vbg.setCustomImage}
                  onClearCustom={vbg.clearCustom}
                  customSrc={vbg.customSrc}
                  ready={vbg.ready}
                  fps={vbg.fps}
                  degraded={vbg.degraded}
                  uploadError={vbg.uploadError}
                />
              </div>
              {/* Stats + shortcuts links */}
              <div className="mt-2 pt-2 border-t border-zinc-700 space-y-0.5">
                <button
                  onClick={() => { setShowStats(s => { if (!s && sttActive) sttStop(); return !s }); setShowParticipants(false); setShowChat(false); setShowWhiteboard(false); closeMenus() }}
                  className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 rounded"
                >
                  Statistiques reseau
                </button>
                <button
                  onClick={() => { setShowShortcutsHelp(true); closeMenus() }}
                  className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 rounded"
                >
                  Raccourcis clavier
                </button>
                <button
                  onClick={() => { setShowNotes(n => !n); setShowParticipants(false); setShowChat(false); setShowStats(false); setShowWhiteboard(false); closeMenus() }}
                  className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 rounded"
                >
                  Notes de reunion
                </button>
              </div>
            </MenuPanel>
          )}
        </div>
      </div>
      {/* Shortcuts help modal */}
      {showShortcutsHelp && <ShortcutsHelp onClose={() => setShowShortcutsHelp(false)} />}
    </div>
  )
}

// ── Menu panel wrapper ────────────────────────────────

function MenuPanel({ children, align, onClick }: { children: React.ReactNode; align?: 'right'; onClick?: React.MouseEventHandler }) {
  return (
    <div
      className={`absolute bottom-full mb-2 bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl z-50 min-w-[220px] ${
        align === 'right' ? 'right-0' : 'left-0'
      }`}
      style={{ zIndex: 9999 }}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

function MenuTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] font-semibold text-zinc-400 mb-2 uppercase tracking-wide">{children}</div>
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 text-[11px] rounded transition-colors ${
        active ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-700'
      }`}
    >
      {children}
    </button>
  )
}

function Separator() {
  return <div className="w-px h-6 bg-zinc-700 mx-0.5" />
}

// ── Device selector ───────────────────────────────────

function DeviceSelector({ kind, label, onDeviceChange, onSwitch, onError }: { kind: MediaDeviceKind; label: string; onDeviceChange?: () => void; onSwitch?: (kind: string, deviceLabel: string) => void; onError?: (kind: string, error: string) => void }) {
  const room = useRoomContext()
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(all => {
      setDevices(all.filter(d => d.kind === kind))
    })
    if (kind === 'audioinput') {
      const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
      const settings = pub?.track?.mediaStreamTrack?.getSettings()
      if (settings?.deviceId) setActiveId(settings.deviceId)
    } else if (kind === 'videoinput') {
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera)
      const settings = pub?.track?.mediaStreamTrack?.getSettings()
      if (settings?.deviceId) setActiveId(settings.deviceId)
    }
  }, [kind, room])

  const switchDevice = async (deviceId: string) => {
    try {
      if (kind === 'audiooutput') {
        // Use setSinkId on audio elements
        const audioEls = document.querySelectorAll('audio')
        for (const el of audioEls) {
          if ('setSinkId' in el) {
            await (el as any).setSinkId(deviceId)
          }
        }
      } else {
        await room.switchActiveDevice(kind, deviceId)
      }
      setActiveId(deviceId)
      const dev = devices.find(d => d.deviceId === deviceId)
      onSwitch?.(kind, dev?.label || deviceId.slice(0, 16))
      onDeviceChange?.()
    } catch (error) {
      onError?.(kind, String(error).slice(0, 512))
    }
  }

  if (devices.length === 0) {
    return <div className="text-[10px] text-zinc-500 py-1">{kind === 'audiooutput' ? 'Sortie systeme par defaut' : 'Aucun peripherique'}</div>
  }

  return (
    <div className="space-y-0.5">
      <div className="text-[10px] text-zinc-500 uppercase mb-0.5">{label}</div>
      {devices.map(d => (
        <button
          key={d.deviceId}
          onClick={() => switchDevice(d.deviceId)}
          className={`w-full text-left px-2 py-1 rounded text-[11px] transition-colors ${
            d.deviceId === activeId ? 'bg-blue-600 text-white' : 'text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          {d.label || `${label} ${d.deviceId.slice(0, 8)}...`}
        </button>
      ))}
    </div>
  )
}

// ── Volume slider ─────────────────────────────────────

function VolumeSlider({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] text-zinc-500">{label}</span>
        <span className="text-[10px] text-zinc-400 font-mono">{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={10}
        step={1}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-blue-500"
      />
      <div className="flex justify-between text-[9px] text-zinc-600 mt-0.5">
        <span>0</span>
        <span>5</span>
        <span>10</span>
      </div>
    </div>
  )
}

// ── EQ badge ──────────────────────────────────────────

function EqBadge({ gains, bands }: { gains: number[]; bands: { label: string; energy: number }[] }) {
  // Always show 7 bars, even when no remote audio
  const displayBands = bands.length > 0 ? bands : BANDS.map(b => ({ label: b.label, energy: -128 }))
  const displayGains = gains.length >= BANDS.length ? gains : BANDS.map(() => 0)
  const anyCorrecting = displayGains.some(g => g < 0)
  const totalGain = displayGains.reduce((a, b) => a + b, 0)
  const barH = (e: number) => Math.max(0, Math.min(16, Math.round((e + 128) / 8)))

  return (
    <span className="flex items-end gap-px" title={`EQ: ${displayGains.map(g => g + 'dB').join(' / ')}`}>
      {displayBands.map((b, i) => {
        const h = barH(b.energy)
        const correcting = (displayGains[i] ?? 0) < 0
        return (
          <span key={i} title={`${b.label}: ${b.energy} dBFS | EQ: ${displayGains[i] ?? 0}dB`}>
            <span className="relative block w-1.5" style={{ height: 16 }}>
              <span
                className={`absolute bottom-0 w-full rounded-t-sm transition-all ${correcting ? 'bg-red-500' : 'bg-emerald-500/70'}`}
                style={{ height: h }}
              />
            </span>
          </span>
        )
      })}
      <span className={`ml-0.5 px-1 py-0.5 rounded text-[9px] font-mono leading-none ${
        anyCorrecting ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300'
      }`}>
        {totalGain}
      </span>
    </span>
  )
}

// ── Remote audio vu-meter ──────────────────────────────

function BufIndicator({ bufferSeconds, buffering, catchingUp }: { bufferSeconds: number; buffering: boolean; catchingUp: boolean }) {
  // Only show when there's buffer content or actively buffering/catching up
  if (bufferSeconds <= 0 && !buffering && !catchingUp) return null
  const color = bufferSeconds <= 0 ? 'bg-emerald-600 text-emerald-100'
    : bufferSeconds <= 5 ? 'bg-yellow-600 text-yellow-100'
    : bufferSeconds <= 10 ? 'bg-orange-600 text-orange-100'
    : 'bg-red-600 text-red-100'
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-mono font-medium ${color} ${catchingUp ? 'animate-pulse' : ''}`}
      title={buffering ? 'Bufferisation en cours (reseau coupe)' : catchingUp ? 'Rattrapage x2 en cours' : `Buffer: ${bufferSeconds}s`}
    >
      BUF {bufferSeconds > 0 ? `${bufferSeconds}s` : ''}
      {catchingUp && <span className="text-[8px]">x2</span>}
    </span>
  )
}

function RemoteVuMeter({ bands }: { bands: { label: string; energy: number }[] }) {
  if (bands.length === 0) {
    return (
      <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
        <div className="h-full w-0 rounded-full bg-zinc-600" />
      </div>
    )
  }
  // Average energy across all bands, map to 0-100%
  const avgEnergy = bands.reduce((s, b) => s + b.energy, 0) / bands.length
  const pct = Math.max(0, Math.min(100, Math.round((avgEnergy + 128) * 100 / 128)))
  return (
    <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

// ── EQ display (larger, for Enceinte menu) ────────────

function EqDisplay({ gains, bands, title }: { gains: number[]; bands: { label: string; energy: number }[]; title?: string }) {
  const displayBands = bands.length > 0 ? bands : BANDS.map(b => ({ label: b.label, energy: -128 }))
  const displayGains = gains.length >= BANDS.length ? gains : BANDS.map(() => 0)
  const barH = (e: number) => Math.max(0, Math.min(32, Math.round((e + 128) / 4)))

  return (
    <div className="mt-2 pt-2 border-t border-zinc-700">
      <div className="text-[10px] text-zinc-500 mb-1">{title ?? 'Spectre 7 bandes'} {bands.length === 0 && '(en attente)'}</div>
      <div className="flex items-end justify-between gap-1 px-1">
        {displayBands.map((b, i) => {
          const h = barH(b.energy)
          const correcting = (displayGains[i] ?? 0) < 0
          const gain = displayGains[i] ?? 0
          return (
            <div key={i} className="flex flex-col items-center gap-0.5">
              <span className="relative block w-3" style={{ height: 32 }}>
                <span
                  className={`absolute bottom-0 w-full rounded-t-sm transition-all ${correcting ? 'bg-red-500' : 'bg-emerald-500/70'}`}
                  style={{ height: h }}
                />
              </span>
              <span className="text-[8px] text-zinc-500 leading-none">{b.label}</span>
              {gain < 0 && (
                <span className="text-[8px] text-red-400 font-mono leading-none">{gain}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Toggle row ────────────────────────────────────────

function ToggleRow({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-1.5 cursor-pointer group" onClick={() => onChange(!checked)}>
      <div>
        <div className="text-xs text-zinc-200 group-hover:text-white">{label}</div>
        <div className="text-[10px] text-zinc-500">{desc}</div>
      </div>
      <div className={`relative w-7 h-3.5 rounded-full flex-shrink-0 ml-3 transition-colors ${checked ? 'bg-blue-600' : 'bg-zinc-600'}`}>
        <div className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-transform ${checked ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
      </div>
    </div>
  )
}

// ── SVG Icons ─────────────────────────────────────────

function MicIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
        <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 16.95A7 7 0 015 12m14 0a7 7 0 01-.11 1.23M12 19v4m-4 0h8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4m-4 0h8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CamIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
        <path d="M1 1l22 22M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3m3-3h6l2 3h4a2 2 0 012 2v9.34" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M23 7l-7 5 7 5V7z" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SpeakerIcon({ muted }: { muted?: boolean }) {
  if (muted) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="23" y1="9" x2="17" y2="15" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="17" y1="9" x2="23" y2="15" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function VideoRecvIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="5" width="14" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 8l-6 4 6 4V8z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function VolumeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M15.54 8.46a5 5 0 010 7.07" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ScreenIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <path d="M8 21h8m-4-4v4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8v4m0 0l2-2m-2 2l-2-2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ScreenOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <path d="M8 21h8m-4-4v4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 10h6M12 7v6" strokeLinecap="round" strokeLinejoin="round" transform="rotate(45 12 10)" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="7 10 12 15 17 10" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="15" x2="12" y2="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PipIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <rect x="12" y="9" width="8" height="6" rx="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
      <path fillRule="evenodd" d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z" clipRule="evenodd" />
    </svg>
  )
}

function RecIcon({ recording }: { recording: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth={2} />
      <circle cx="12" cy="12" r="4" fill={recording ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={recording ? 0 : 2}>
        {recording && <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />}
      </circle>
    </svg>
  )
}

function CcIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M10 10.5a1.5 1.5 0 00-3 0v3a1.5 1.5 0 003 0" />
      <path d="M17 10.5a1.5 1.5 0 00-3 0v3a1.5 1.5 0 003 0" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  )
}

function HandIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M18 11V6a2 2 0 00-4 0v1M14 10V4a2 2 0 00-4 0v6M10 10V5a2 2 0 00-4 0v9l-1.76-1.76a2 2 0 00-2.83 2.83L7 21h10a2 2 0 002-2v-5a2 2 0 00-4 0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ReactionIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ParticipantsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function BreakoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="2" y="3" width="8" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="14" y="3" width="8" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="2" y="14" width="8" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="14" y="14" width="8" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function FocusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <rect x="9" y="5" width="10" height="14" rx="1" />
    </svg>
  )
}

function WhiteboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="3" y="3" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 21h8M12 17v4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MinimizeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  )
}

// ── MiniPlayer: Firefox PiP fallback (draggable 200x150) ──

function MiniPlayer({ activeSpeaker, onBack, onQuit, isMicOn, isCamOn, toggleMic, toggleCam }: {
  activeSpeaker?: { identity: string; name?: string; getTrackPublication?: (source: any) => any } | null
  onBack: () => void
  onQuit: () => void
  isMicOn: boolean
  isCamOn: boolean
  toggleMic: () => void
  toggleCam: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    dragState.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top }

    const onMove = (ev: MouseEvent) => {
      const ds = dragState.current
      if (!ds || !el) return
      const x = ds.origX + (ev.clientX - ds.startX)
      const y = ds.origY + (ev.clientY - ds.startY)
      el.style.left = `${Math.max(0, Math.min(window.innerWidth - 200, x))}px`
      el.style.top = `${Math.max(0, Math.min(window.innerHeight - 150, y))}px`
      el.style.right = 'auto'
      el.style.bottom = 'auto'
    }
    const onUp = () => {
      dragState.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  // Get video track from active speaker
  const videoTrack = activeSpeaker?.getTrackPublication?.(Track.Source.Camera)?.track
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !videoTrack) return
    const mediaTrack = videoTrack.mediaStreamTrack
    if (!mediaTrack) return
    video.srcObject = new MediaStream([mediaTrack])
    video.play().catch(() => {})
    return () => { video.srcObject = null }
  }, [videoTrack])

  return (
    <div
      ref={ref}
      onMouseDown={onMouseDown}
      className="fixed z-[9999] cursor-move select-none"
      style={{ bottom: 24, right: 24, width: 200, height: 150 }}
    >
      <div className="w-full h-full rounded-lg overflow-hidden bg-zinc-900 border border-zinc-700 shadow-2xl flex flex-col">
        {/* Video */}
        <div className="flex-1 relative bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          <div className="absolute top-1 left-1 bg-black/60 rounded px-1 text-[9px] text-white">
            {activeSpeaker?.name || activeSpeaker?.identity || 'PiP'}
          </div>
        </div>
        {/* Controls */}
        <div className="flex items-center justify-between px-2 py-1 bg-zinc-800 shrink-0">
          <div className="flex gap-1">
            <button onClick={toggleMic} className={`px-1.5 py-0.5 rounded text-[9px] ${isMicOn ? 'bg-zinc-700 text-zinc-300' : 'bg-red-600 text-white'}`}>
              {isMicOn ? 'Mic' : 'Mic off'}
            </button>
            <button onClick={toggleCam} className={`px-1.5 py-0.5 rounded text-[9px] ${isCamOn ? 'bg-zinc-700 text-zinc-300' : 'bg-red-600 text-white'}`}>
              {isCamOn ? 'Cam' : 'Cam off'}
            </button>
          </div>
          <div className="flex gap-1">
            <button onClick={onBack} className="px-1.5 py-0.5 rounded text-[9px] bg-blue-600 text-white hover:bg-blue-700">
              Retour
            </button>
            <button onClick={onQuit} className="px-1.5 py-0.5 rounded text-[9px] bg-red-600 text-white hover:bg-red-700">
              Quitter
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
